import { useState, useEffect } from "react";

// ── CONSTANTS ──────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { id: "va", label: "Virtual Assistant", icon: "◈" },
  { id: "admin", label: "Administrative & Executive Support", icon: "■" },
  { id: "ops", label: "Operations & Project Coordination", icon: "⬡" },
  { id: "customer", label: "Customer Service & Client Support", icon: "▸" },
  { id: "data", label: "Data Entry & Research", icon: "◎" },
  { id: "other", label: "Something else", icon: "✦" },
];

const ENVIRONMENT_OPTIONS = [
  { id: "low_cognitive", label: "Straightforward & Clear", resumeLabel: "Thrives in structured, well-defined environments", icon: "◎",
    desc: "Straightforward tasks with clear instructions — think building spreadsheets, sending follow-up emails, scheduling. No heavy decision-making required." },
  { id: "high_autonomy", label: "High-autonomy", resumeLabel: "Self-directed, minimal oversight required", icon: "⬡",
    desc: "Give me the goal and get out of my way. No micromanaging — I figure out the path and deliver the result." },
  { id: "structured", label: "Structured & process-driven", resumeLabel: "Structured & process-driven", icon: "■",
    desc: "I thrive when there's a system, a workflow, a clear way things get done. I follow processes well and improve them even better." },
];

const LOCATION_OPTIONS = ["Remote only", "In-person only", "Hybrid", "Open to all"];
const WORK_TYPE_OPTIONS = ["Part-time only", "Full-time only", "Open to both"];
const TIMEZONE_OPTIONS = ["Eastern (ET)", "Central (CT)", "Mountain (MT)", "Pacific (PT)", "Atlantic (AST)", "Hawaii (HST)", "GMT/UTC", "CET (Europe)", "Other"];

const SKILLS_BY_ROLE = {
  va: ["Calendar & Schedule Management", "Email Inbox Management", "Travel Coordination", "Meeting Coordination", "Document Preparation", "Research & Summarizing", "Data Entry", "Client Communication", "Invoice & Billing Support", "Social Media Scheduling", "File Organization", "CRM Management", "Appointment Setting", "Expense Tracking"],
  admin: ["Executive Calendar Management", "Board & Meeting Preparation", "Correspondence Management", "Report Generation", "Budget Tracking", "Vendor Coordination", "Compliance Documentation", "Onboarding Support", "Process Documentation", "Cross-Departmental Communication", "Records Management", "Event Coordination"],
  ops: ["Workflow Management", "SOP Development", "Project Tracking", "KPI Reporting", "Process Improvement", "Resource Coordination", "Budget Management", "Stakeholder Communication", "Systems Implementation", "Data Analysis", "Risk Management", "Capacity Planning"],
  customer: ["Ticket & Case Management", "Client Onboarding", "Issue Resolution", "Knowledge Base Management", "Escalation Handling", "Customer Follow-Up", "Satisfaction Tracking", "CRM Documentation", "Churn Prevention", "Billing Support"],
  data: ["Data Verification & Auditing", "Database Management", "Spreadsheet Management", "Report Building", "Web Research", "Data Cleaning", "Accuracy QA", "Data Entry", "Competitive Research", "Market Research"],
  other: ["Project Management", "Written Communication", "Client Relations", "Administrative Support", "Research", "Data Management", "Process Documentation", "Team Coordination"],
};

const TOOLS_BY_ROLE = {
  universal: ["Google Workspace", "Microsoft Office Suite", "Zoom", "Slack", "Gmail / Outlook", "Google Drive", "Dropbox"],
  va: ["Calendly", "Acuity Scheduling", "Trello", "Notion", "Asana", "Monday.com", "DocuSign", "LastPass", "Loom", "ClickUp"],
  admin: ["Microsoft Outlook", "SharePoint", "DocuSign", "Concur", "QuickBooks", "Salesforce", "SAP", "Netsuite", "Workday"],
  ops: ["Asana", "Monday.com", "ClickUp", "Notion", "Airtable", "Zapier", "Make (Integromat)", "Google Analytics", "Looker Studio", "Jira", "Basecamp"],
  customer: ["Zendesk", "Freshdesk", "HubSpot", "Intercom", "Salesforce", "Front", "Help Scout", "Gorgias", "Kustomer"],
  data: ["Excel (Advanced)", "Google Sheets", "Airtable", "Tableau", "Power BI", "QuickBooks", "SQL (Basic)", "Python (Basic)"],
  ai: ["ChatGPT", "Claude", "Canva", "CapCut", "Jasper", "Midjourney", "Grammarly", "Otter.ai"],
};

const CERTS_BY_ROLE = {
  va: ["Google Project Management Certificate", "Calendly Certified", "HubSpot CRM Certified", "Asana Academy Certified", "Notion Essentials Badge"],
  admin: ["Microsoft Office Specialist (MOS)", "Certified Administrative Professional (CAP)", "Google Workspace Certified", "Salesforce Admin Certification", "QuickBooks Certified User"],
  ops: ["PMP — Project Management Professional", "Google Project Management Certificate", "Lean Six Sigma (Yellow/Green Belt)", "Certified ScrumMaster (CSM)", "Airtable Certified"],
  customer: ["HubSpot Customer Service Certified", "Zendesk Support Administrator", "Salesforce Service Cloud Consultant", "CXPA Customer Experience Certificate"],
  data: ["Google Data Analytics Certificate", "Microsoft Excel Expert (MOS)", "Tableau Desktop Specialist", "IBM Data Analyst Certificate", "SQL for Data Science (Coursera)"],
  other: ["Google Project Management Certificate", "HubSpot Content Marketing", "Coursera Business Foundations", "LinkedIn Learning Certificate"],
};

const WORKING_STYLE_OPTIONS = [
  "Async-first", "Self-directed", "Detail-oriented", "Calm under pressure",
  "Discreet & trustworthy", "Process-driven", "Collaborative by nature",
  "Clear communicator", "Reliable & consistent", "Deadline-focused",
  "Quick learner", "Low-maintenance", "Solutions-oriented", "Proactive",
];

// ── CLARITY PHRASES ────────────────────────────────────────────────────────

const CLARITY_PHRASES = {
  high: [
    "You didn't guess. You evaluated. Now go apply.",
    "This role has your name on it. Don't overthink it.",
    "The numbers say yes. Trust them.",
    "Everything lines up. Your next step is the submit button.",
    "You're not hoping this works. You know it works.",
    "Strong match. Weak competition. Apply today.",
    "JobLanded did the math. The answer is yes.",
    "This is exactly what your profile was built for.",
    "Your background speaks directly to this role.",
    "You're ready. This is the one.",
  ],
  medium: [
    "Apply if this role excites you. Excitement fills a lot of gaps.",
    "Not perfect on paper — but neither is anyone else.",
    "You meet the core. The rest is what interviews are for.",
    "Partial alignment isn't a dealbreaker. It's a starting point.",
    "You have enough to get in the door. What happens next is up to you.",
    "The fit isn't perfect — but perfect rarely applies anyway.",
    "You're competitive enough to be considered. That's all you need.",
    "Not a slam dunk — but absolutely worth the shot.",
    "Some gaps exist. That's not a no — that's a conversation starter.",
    "Apply thoughtfully. You've got a real shot.",
  ],
  low: [
    "JobLanded just saved you from a long shot. On to the next.",
    "Your profile deserves a better match. Keep going.",
    "Not every job deserves your application. This one doesn't.",
    "The gaps here are real. Your energy is better spent elsewhere.",
    "Honest answer? Skip it. Your time is too valuable for long shots.",
    "This isn't a no forever — it's a not right now with this posting.",
    "You came here for clarity. Here it is: keep looking.",
    "Low alignment doesn't mean low worth. It means wrong fit.",
    "JobLanded filtered this out so you didn't have to wonder.",
    "Your next strong match is one posting away.",
  ],
};

const TIME_PHRASES = [
  "You saved approximately 45 minutes today.",
  "That would have taken you an hour without JobLanded.",
  "45 minutes back in your day. Use them well.",
  "Your stolen hours just got a little less stolen.",
  "This evaluation took minutes. The old way took all evening.",
  "You just did in minutes what used to take hours.",
  "One less thing eating your bandwidth today.",
  "Clarity delivered. Time returned.",
  "JobLanded handled the hard part. You just have to decide.",
  "Less guessing. More deciding. That's the whole point.",
];

const DOCS_READY_PHRASES = [
  "Here are your documents — ready when you are.",
  "Everything you need to apply is right here.",
  "Your application package is complete.",
  "Three documents. One application. Zero guesswork.",
  "Built from your profile. Tailored to this job. Ready to go.",
];

const LOADING_PHRASES = [
  "Take a deep breath. Your documents are being built.",
  "While you wait — you're already ahead of everyone who didn't apply.",
  "This takes about 30 seconds. You've got this.",
  "Your resume is being tailored right now. No guessing required.",
  "Clarity incoming. Hang tight.",
  "Working on it. Breathe. You've done the hard part.",
  "Building something worth applying with. Almost there.",
  "Your next opportunity is being prepared. Stay calm.",
];

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
}

function randomFrom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function formatTemplateLabel(id = "") {
  if (id === "ats") return "ATS";
  if (id === "traditional") return "Traditional";
  if (id === "modern") return "Modern";
  return id ? id.charAt(0).toUpperCase() + id.slice(1) : "";
}

function recommendResumeTemplate(jobtitle = "", jobdesc = "") {
  const text = `${jobtitle}
${jobdesc}`.toLowerCase();

  let ats = 0;
  let traditional = 0;
  let modern = 0;

  if (/applicant tracking system|ats|workday|greenhouse|lever|icims|taleo/.test(text)) ats += 4;
  if (/compliance|regulated|policy|policies|documentation|documentation-heavy|reporting|audit|accuracy|data entry|records|record keeping/.test(text)) ats += 3;
  if (/corporate|enterprise|operations|coordinator|administrative assistant|executive assistant|customer support|analyst/.test(text)) ats += 2;
  if (/must be detail-oriented|required qualifications|minimum qualifications|preferred qualifications/.test(text)) ats += 2;

  if (/executive assistant|administrative|admin|assistant|coordinator|office manager|scheduler|calendar|inbox|travel coordination/.test(text)) traditional += 4;
  if (/professionalism|organized|stakeholders|cross-functional|support leadership|board|meeting preparation/.test(text)) traditional += 3;
  if (/nonprofit|healthcare|education|university|government|law firm|financial/.test(text)) traditional += 2;

  if (/startup|fast-paced|wear many hats|creator|content|marketing|brand|social media|community|design|creative/.test(text)) modern += 4;
  if (/notion|slack|asana|clickup|airtable|canva|figma/.test(text)) modern += 2;
  if (/growth|innovation|entrepreneurial|modern|digital-first|build from scratch/.test(text)) modern += 2;

  const scores = [
    { id: "ats",         score: ats,         why: "Best for ATS-heavy, structured, corporate, or compliance-focused roles." },
    { id: "traditional", score: traditional,  why: "Best for administrative, executive support, and polished professional roles." },
    { id: "modern",      score: modern,       why: "Best for startup, creative, marketing, and brand-forward roles." },
  ].sort((a, b) => b.score - a.score);

  const winner   = scores[0];
  const runnerUp = scores[1];
  const confidence = winner.score >= runnerUp.score + 2 ? "high" : "medium";

  return {
    template:    winner.id,
    confidence,
    reason:      winner.why,
    scores:      { ats, traditional, modern },
  };
}

// ── LIGHT / DARK THEME ─────────────────────────────────────────────────────

function makeColors(dark) {
  return dark ? {
    navy:       "#1C2B3A",
    navyCard:   "rgba(42,127,111,0.06)",
    teal:       "#2A7F6F",
    tealBright: "#3A9A87",
    tealLight:  "#7BBFB3",
    tealPale:   "#EBF5F3",
    tealMid:    "#C8E8E3",
    offWhite:   "#F8F5F3",
    bluGray:    "#8FA3B2",
    slate:      "#4A5F72",
    text:       "#F8F5F3",
    textSub:    "#8FA3B2",
    textMuted:  "#4A5F72",
    border:     "rgba(123,191,179,0.15)",
    borderHov:  "rgba(123,191,179,0.35)",
    card:       "rgba(42,127,111,0.06)",
    cardHov:    "rgba(42,127,111,0.1)",
    bg:         "#1C2B3A",
    surface:    "#213243",
    modalBg:    "#1C2B3A",
    inputBg:    "rgba(42,127,111,0.06)",
  } : {
    navy:       "#1C2B3A",
    navyCard:   "#F0F7F5",
    teal:       "#2A7F6F",
    tealBright: "#2A7F6F",
    tealLight:  "#3A9A87",
    tealPale:   "#EBF5F3",
    tealMid:    "#C8E8E3",
    offWhite:   "#1C2B3A",
    bluGray:    "#4A5F72",
    slate:      "#6B8090",
    text:       "#1C2B3A",
    textSub:    "#4A5F72",
    textMuted:  "#8FA3B2",
    border:     "rgba(42,127,111,0.15)",
    borderHov:  "rgba(42,127,111,0.35)",
    card:       "#F0F7F5",
    cardHov:    "#E5F3F0",
    bg:         "#FAFAF8",
    surface:    "#FFFFFF",
    modalBg:    "#FFFFFF",
    inputBg:    "#FFFFFF",
  };
}

// ── HELPERS ────────────────────────────────────────────────────────────────

function getSkillsForRoles(roles) {
  const skills = new Set();
  roles.forEach(r => (SKILLS_BY_ROLE[r] || []).forEach(s => skills.add(s)));
  return [...skills];
}

function getToolsForRoles(roles) {
  const tools = new Set();
  TOOLS_BY_ROLE.universal.forEach(t => tools.add(t));
  TOOLS_BY_ROLE.ai.forEach(t => tools.add(t));
  roles.forEach(r => (TOOLS_BY_ROLE[r] || []).forEach(t => tools.add(t)));
  return [...tools];
}

function getCertsForRoles(roles) {
  const certs = new Set();
  roles.forEach(r => (CERTS_BY_ROLE[r] || []).forEach(c => certs.add(c)));
  if (certs.size === 0) CERTS_BY_ROLE.other.forEach(c => certs.add(c));
  return [...certs];
}

function validateResume(text) {
  if (!text || text.trim().length < 50) return null;

  const t = text.toLowerCase();

  // Catch cover letters
  const looksLikeLetter = /dear hiring team|dear recruiter|dear sir|dear madam|sincerely|best regards|i am excited to apply|i would welcome the opportunity|i am writing to apply|please consider my application/i.test(t);
  if (looksLikeLetter) {
    return "This looks like a cover letter, not a resume. Please paste your full resume — job titles, company names, dates, and bullet points for each role.";
  }

  // Catch one-pagers / bios
  const looksLikeOnePager = /key highlights|working style|tools & tech|available immediately|references available upon request|core competencies|professional profile/i.test(t);

  const hasYear = /\b(19|20)\d{2}\b/.test(t);
  const hasMonth = /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\b/.test(t);
  const hasDate = hasYear || hasMonth;
  const roleMatches = text.match(/\b(manager|director|coordinator|assistant|specialist|analyst|representative|associate|officer|executive|administrator|lead|supervisor|registrar|advocate|ceo|founder|consultant)\b/gi) || [];
  const employerMatches = text.match(/\b(inc|llc|corp|ltd|group|university|college|school|hospital|clinic|agency|foundation|fidelity|meta|google|amazon)\b/gi) || [];

  const weakStructure = roleMatches.length < 1 || !hasDate;

  if (looksLikeOnePager && weakStructure) {
    return "This looks like a one-pager or profile summary, not a full resume. Please paste your complete work history — each role needs a job title, company name, dates, and bullet points.";
  }

  if (weakStructure && employerMatches.length < 1) {
    return "Your resume may be incomplete. Make sure to include at least 2 roles with: job title, company name, dates, and bullet points for each.";
  }

  return null;
}

// ── PROMPT BUILDERS ────────────────────────────────────────────────────────

function buildProfileContext(profile) {
  const roles = ROLE_OPTIONS.filter(r => profile.roles?.includes(r.id)).map(r => r.label).join(", ");
  const env = ENVIRONMENT_OPTIONS.find(e => e.id === profile.environment);
  const skills = (profile.coreSkills || []).join(", ");
  const tools = (profile.tools || []).join(", ");
  const style = (profile.workingStyle || []).join(", ");

  return `You are a job application assistant for ${profile.name || "the user"}.

PROFILE:
- Name: ${profile.name || "Not provided"}
- Target roles: ${roles || "General remote/admin work"}
- Environment preference: ${env ? env.resumeLabel : "Not specified"}
- Target hourly rate: $${profile.rate || "25"}/hr minimum
- Target hours/week: ${profile.hoursMin || "20"}–${profile.hoursMax || "40"} hours
- Work type: ${profile.workType || "Open to both"}
- Location: ${profile.location || "Remote only"} | Timezone: ${profile.timezone || "Not specified"}
- Core Skills: ${skills || "See resume"}
- Tools & Tech: ${tools || "See resume"}
- Working Style: ${style || "Professional, reliable, async-capable"}

CONTACT INFO FOR RESUME:
- Name: ${profile.name || ""}
- Location: Remote — ${profile.city || ""}${profile.timezone ? " / " + profile.timezone : ""}
- Phone: ${profile.phone || ""}
- Email: ${profile.email || ""}
${profile.linkedin ? "- LinkedIn: " + profile.linkedin : ""}

EDUCATION:
${profile.education?.length ? profile.education.map(e => `${e.degree} — ${e.institution}${e.year ? " · " + e.year : ""}${e.gpa ? " · GPA " + e.gpa : ""}`).join("\n") : "Not provided"}
${profile.certifications ? "Certifications: " + profile.certifications : ""}

NOTABLE ACHIEVEMENTS:
${profile.achievements || "None provided"}

RESUME (base document — do not invent information not in here):
${profile.resume || "Not provided"}

SCORING CRITERIA:
✓ BOOST: matches role type, environment preference, rate ≥ $${profile.rate || "25"}/hr, hours in range, location match, async culture, clear scope
✗ PENALIZE: rate below $${profile.rate || "25"}/hr, hours out of range, location conflict, high meetings, micromanagement, "wear many hats", vague scope
${profile.environment === "low_cognitive" ? "✗ PENALIZE HEAVILY: complex decisions, strategic leadership, ambiguous problem-solving" : ""}
${profile.environment === "high_autonomy" ? "✗ PENALIZE HEAVILY: micromanagement signals, excessive check-ins, rigid oversight" : ""}
${profile.environment === "structured" ? "✓ BOOST: clear SOPs, defined workflows. ✗ PENALIZE: vague scope, chaos signals, startup ambiguity" : ""}`;
}

function buildPrompt(profile, company, jobtitle, jobdesc) {
  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });

  return `${buildProfileContext(profile)}

---
JOB POSTING:
Company: ${company || "Not specified"}
Title: ${jobtitle || "Not specified"}
Description:
${jobdesc}

---
Generate ALL sections below using EXACTLY these headers. Do not skip any.
CRITICAL: Every section MUST start with its ===HEADER=== marker on its own line. Never merge sections. Never skip a marker. Output all 8 sections even if brief.

===REALITY_FLAGS===
Scan for: no salary range, vague structure, generic/templated language, evergreen signals, scope creep ("wear many hats"), part-time claim with full-time workload, no specific team named, unrealistic expectations.
If 2+ red flags: list each starting with ⚠
If legitimate: write CLEAR

===FIT_SCORE===
Single number 0–100 only.

===FIT_VERDICT===
One short sentence.

===FIT_REASONING===
2–3 sentences: alignment, gaps, rate/hours/location match assessment.

===KEYWORDS===
Extract keywords from this job description and compare to the candidate's profile.

FORMAT EXACTLY AS:
✓ COVERED — [list keywords already in candidate's profile/resume]
⚠ ADD IF YOU HAVE IT — [skills or tools mentioned in JD not in profile but candidate might have]
✕ NOT IN PROFILE — [hard requirements in JD that candidate doesn't appear to have]

===RESUME_FULL===
Generate a complete, tailored, one-page resume for ${profile.name || "the applicant"} targeted to this specific job.

STRICT INSTRUCTIONS:
— Use ONLY information from the candidate's profile and resume above. Do not invent anything.
— CRITICAL: Copy every company name EXACTLY as written in the resume. Never substitute generic placeholders like "Consulting Firm" or "Previous Role" — use the actual company name.
— CRITICAL: Copy every job title EXACTLY as written in the resume. Never paraphrase or generalize titles.
— CRITICAL: Copy all dates EXACTLY as written in the resume. Never change or omit dates.
— Include EVERY role from the resume — do not drop any positions.
— For the 1–2 most relevant roles: rewrite 3–4 bullets to speak to THIS job's requirements
— For all other roles: keep bullets close to original, trim to 2 bullets max
— Mark rewritten bullets with → at start, keep original bullets with - at start
— Skills: list maximum 10 most relevant skills. Do not exceed this.
— If no notable achievements provided, omit the ACHIEVEMENTS section entirely
— Working Style: never include "low-cognitive-load" — use the environment label from profile instead
— Total word count: 450–550 words maximum

OUTPUT FORMAT — Use these exact section markers. Each section starts with the marker on its own line:

[TITLE]
${profile.name || "FULL NAME"}
[Role Title that matches the job posting]
[One-line tagline: years experience · key strength · availability]

[SUMMARY]
2-3 sentences. Professional summary connecting candidate's experience to THIS specific role. Focus on their strongest qualifications for this job.

[SKILLS]
List exactly 10 skills, one per line, most relevant to this job first. Pull from profile skills and add job-relevant ones from their resume.

[EXPERIENCE]
Format each role exactly as:
JOBTITLE | COMPANY | START–END
→ tailored bullet point speaking to this job's requirements
→ tailored bullet point
- original bullet kept from resume

Include all roles from the resume. Most relevant roles get 3-4 bullets, others get 2.

[ACHIEVEMENTS]
Only include if achievements were provided. Format as:
- Achievement description with specific numbers/impact

[REFERENCES]
References available upon request · Open to ${ROLE_OPTIONS.filter(r => profile.roles?.includes(r.id)).map(r => r.label).join(", ") || "Remote"} roles

===COVER_LETTER===
Complete professional cover letter, under 300 words.
Follow this EXACT structure:

Paragraph 1 — Opening: State the role and company by name. Lead with years of experience and single strongest value proposition for THIS specific role. One sentence on what makes them well-positioned.

Paragraph 2 — Evidence: Start with "My experience as [their actual job title] at [their actual company] has required me to master [2-3 specific skills from this job posting]." Then include EXACTLY 3 bullet points using real achievements with specific numbers from their resume:
* [Achievement label] — [specific metric or outcome from resume]
* [Achievement label] — [specific metric or outcome from resume]  
* [Achievement label] — [specific metric or outcome from resume]

Paragraph 3 — Bridge: One sentence connecting their background to this role's environment. Then their working style + relevant tools + why they fit this specific role.

Closing line — Distinctive, confident. Reference their unique angle (e.g. "business owner's mindset", "async-first operator", "compliance-trained"). NOT generic "administrative expertise".

- Address to "Hiring Team" unless a specific name appears in the job description
- Tone: calm, reliable, direct — never sycophantic
- Include: [City, State] · ${currentDate} header, salutation, closing
- Sign as ${profile.name || "the applicant"}
- CRITICAL: Use REAL numbers from their resume. Never invent metrics. If no numbers exist use specific outcomes instead.

===FOLLOWUP_EMAIL===
Subject: [subject line]
[Email body — under 100 words, professional and warm]

===KEY_NOTES===
⚠ APPLICATION INSTRUCTIONS
⚠ DO NOT CONTACT warnings
⚠ REQUIRED ATTACHMENTS
⚠ COVER LETTER PROMPTS
⚠ SCREENING QUESTIONS
⚠ DEADLINES
⚠ HOURS/RATE CONFIRMATION
⚠ RED FLAGS vs this candidate's targets
If nothing notable: No special instructions found — standard application process.`;
}

function buildQuickAnswers(profile) {
  const roles = ROLE_OPTIONS.filter(r => profile.roles?.includes(r.id)).map(r => r.label).join(", ");
  const env = ENVIRONMENT_OPTIONS.find(e => e.id === profile.environment);
  const rate = profile.rate || "25";
  const hoursMin = profile.hoursMin || "20";
  const hoursMax = profile.hoursMax || "40";
  const workType = profile.workType || "part-time";
  const location = profile.location || "remote";
  const tz = profile.timezone || "flexible";
  const style = (profile.workingStyle || []).slice(0, 4).join(", ");

  return [
    { category: "Professional Summary", icon: "◈", items: [
      { label: "Standard (2–3 sentences)", text: `Experienced ${roles || "remote operations"} professional with a background in structured, reliable delivery. I work best in ${env ? env.resumeLabel.toLowerCase() : "focused"} environments — ${style || "self-directed, detail-oriented, async-capable"}. Currently seeking ${workType.toLowerCase()} ${location.toLowerCase()} opportunities where I can contribute immediately.` },
      { label: "Short (1 sentence)", text: `${roles || "Remote operations"} professional seeking ${workType.toLowerCase()} ${location.toLowerCase()} work — ${style || "reliable, organized, and available immediately"}.` },
    ]},
    { category: "Availability & Hours", icon: "◎", items: [
      { label: "Standard", text: `I'm available ${workType.toLowerCase()}, ${hoursMin}–${hoursMax} hours per week. I operate in ${tz} and am available during standard business hours. I'm self-directed and async-capable — no set shift times required.` },
      { label: "Start date", text: "I'm available to start immediately." },
      { label: "Timezone", text: `I'm in ${tz} and available during full US business hours. Experienced working across time zones asynchronously.` },
    ]},
    { category: "Rate & Compensation", icon: "✦", items: [
      { label: "Rate confirmation", text: `My target rate is $${rate}/hour for ${workType.toLowerCase()} work. Open to discussing structure — hourly, project-based, or retainer — based on scope.` },
      { label: "Salary expectations", text: `For ${workType.toLowerCase()} ${location.toLowerCase()} work in ${roles || "this field"}, I'm targeting $${rate}/hour. Flexible on structure.` },
    ]},
    { category: "Work Environment", icon: "⬡", items: [
      { label: "Environment preference", text: env ? `I do my best work in ${env.resumeLabel.toLowerCase()} environments. That's where I add the most consistent value.` : `I thrive in focused, well-defined environments where expectations are clear.` },
      { label: `Why ${location.toLowerCase()}`, text: location === "Remote only" ? `I've been working remotely for years — it's not a lifestyle preference, it's how I do my best work. Self-directed, proactive communicator, no oversight needed to stay on task.` : `I'm comfortable in ${location.toLowerCase()} arrangements and adapt well to different work structures.` },
    ]},
    { category: "Why Part-Time / Contract", icon: "▸", items: [
      { label: "Why part-time", text: `I'm intentionally seeking ${workType.toLowerCase()} work. I do focused, high-quality work within a defined scope — that's where I add the most value.` },
      { label: "Why contract", text: `I prefer contract arrangements because they keep scope clear and expectations defined for both sides. I deliver reliably within that scope and am easy to work with.` },
    ]},
  ];
}


// Unified download entry point — routes by docType, saves templateId to app
async function downloadDocx(template, docType, content, filename, profile, jobTitle, company) {
  try {
    const response = await fetch('/api/docx', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ template, docType, content, profile, jobTitle, company }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Server error ${response.status}`);
    }

    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="([^"]+)"/i);
    const serverFilename = match?.[1] || filename || "Document.docx";

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = serverFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

  } catch (err) {
    console.error('docx build error:', err);
    const uri = 'data:text/plain;charset=utf-8,' + encodeURIComponent(content);
    const a = document.createElement('a');
    a.href = uri;
    a.download = (filename || "Document.docx").replace(/\.docx$/i, '.txt');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    alert('Download failed — please try again or use the Copy button to grab your document.');
  }
}

// ── TEMPLATE PICKER & DOWNLOAD ─────────────────────────────────────────────

function downloadText(filename, content) {
  const uri = "data:text/plain;charset=utf-8," + encodeURIComponent(content);
  const a = document.createElement("a"); a.href = uri; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
}

// ── COPY BUTTON ────────────────────────────────────────────────────────────

function CopyButton({ text, small, label, C }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(fallback);
    } else { fallback(); }
  };
  const fallback = () => {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;";
    document.body.appendChild(ta); ta.focus(); ta.select();
    try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
    document.body.removeChild(ta);
  };
  return (
    <button onClick={copy} style={{ background: copied ? `rgba(42,127,111,0.2)` : "rgba(255,255,255,0.06)", border: "none", color: copied ? C.tealBright : C.textSub, padding: small ? "5px 10px" : "6px 14px", borderRadius: 7, fontSize: small ? "0.72rem" : "0.77rem", cursor: "pointer", fontFamily: "inherit", fontWeight: 500, transition: "all 0.2s", whiteSpace: "nowrap" }}>
      {copied ? "✓ Copied!" : (label || "Copy")}
    </button>
  );
}

// ── TEMPLATE THUMBNAILS ───────────────────────────────────────────────────

function TemplateThumbnail({ type, size = "full", C }) {
  const s = size === "mini" ? { w: 40, h: 52 } : { w: "100%", h: undefined };
  const isMini = size === "mini";

  // Wireframe blocks — traditional
  if (type === "executive" || type === "traditional") {
    return (
      <div style={{ width: s.w, height: s.h, aspectRatio: isMini ? undefined : "8.5/11", display: "grid", gridTemplateColumns: "35% 65%", overflow: "hidden", borderRadius: isMini ? 4 : 6, background: "#fff", border: `1px solid rgba(123,191,179,0.15)`, flexShrink: 0 }}>
        <div style={{ background: "#1C2B3A", padding: isMini ? "4px 3px" : "10px 7px" }}>
          <div style={{ width: "70%", height: isMini ? 3 : 6, background: "#fff", borderRadius: 1, marginBottom: isMini ? 2 : 3 }} />
          <div style={{ width: "50%", height: isMini ? 2 : 3, background: "#7BBFB3", borderRadius: 1, marginBottom: isMini ? 4 : 10 }} />
          <div style={{ width: "40%", height: isMini ? 1 : 2, background: "#2A7F6F", borderRadius: 1, marginBottom: isMini ? 2 : 4 }} />
          {[80,65,70].map((w,i) => <div key={i} style={{ width: `${w}%`, height: isMini ? 1 : 2, background: "rgba(143,163,178,0.4)", borderRadius: 1, marginBottom: isMini ? 2 : 3 }} />)}
          {!isMini && <>
            <div style={{ width: "40%", height: 2, background: "#2A7F6F", borderRadius: 1, margin: "8px 0 4px" }} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginBottom: 6 }}>
              {[22,28,18,25,22,20].map((w,i) => <div key={i} style={{ width: w, height: 5, background: "rgba(42,127,111,0.3)", borderRadius: 1 }} />)}
            </div>
            <div style={{ width: "40%", height: 2, background: "#2A7F6F", borderRadius: 1, margin: "6px 0 4px" }} />
            {[60,55,50].map((w,i) => <div key={i} style={{ width: `${w}%`, height: 2, background: "rgba(143,163,178,0.4)", borderRadius: 1, marginBottom: 3 }} />)}
          </>}
        </div>
        <div style={{ background: "#fff", padding: isMini ? "4px 3px" : "10px 8px" }}>
          <div style={{ width: "60%", height: isMini ? 2 : 3, background: "#2A7F6F", borderRadius: 1, marginBottom: isMini ? 3 : 4 }} />
          {(isMini ? [90,75,60] : [90,78,65,90,78,90,65]).map((w,i) => <div key={i} style={{ width: `${w}%`, height: isMini ? 1 : 2, background: "#ddd", borderRadius: 1, marginBottom: isMini ? 2 : 3 }} />)}
          {!isMini && <>
            <div style={{ height: 1, background: "linear-gradient(to right,rgba(42,127,111,0.3),transparent)", margin: "6px 0" }} />
            <div style={{ width: "45%", height: 3, background: "#2A7F6F", borderRadius: 1, marginBottom: 4 }} />
            {[90,78,90,65].map((w,i) => <div key={i} style={{ width: `${w}%`, height: 2, background: "#ddd", borderRadius: 1, marginBottom: 3 }} />)}
            <div style={{ height: 1, background: "linear-gradient(to right,rgba(42,127,111,0.3),transparent)", margin: "6px 0" }} />
            <div style={{ width: "40%", height: 3, background: "#2A7F6F", borderRadius: 1, marginBottom: 4 }} />
            {[90,78,65].map((w,i) => <div key={i} style={{ width: `${w}%`, height: 2, background: "#ddd", borderRadius: 1, marginBottom: 3 }} />)}
          </>}
        </div>
      </div>
    );
  }

  // Wireframe blocks — modern
  if (type === "modern") {
    return (
      <div style={{ width: s.w, height: s.h, aspectRatio: isMini ? undefined : "8.5/11", display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: isMini ? 4 : 6, background: "#fff", border: `1px solid rgba(123,191,179,0.15)`, flexShrink: 0 }}>
        <div style={{ height: isMini ? 3 : 5, background: "linear-gradient(to right,#1C2B3A,#2A7F6F,#3A9A87)", flexShrink: 0 }} />
        <div style={{ padding: isMini ? "4px 3px 3px" : "8px 8px 6px", borderBottom: `${isMini ? 1 : 2}px solid #2A7F6F` }}>
          <div style={{ width: "55%", height: isMini ? 3 : 7, background: "#1C2B3A", borderRadius: 1, marginBottom: isMini ? 2 : 3 }} />
          <div style={{ width: "35%", height: isMini ? 2 : 3, background: "#2A7F6F", borderRadius: 1, marginBottom: isMini ? 2 : 4 }} />
          {!isMini && <div style={{ display: "flex", gap: 6 }}>
            {[20,25,30].map((w,i) => <div key={i} style={{ width: w, height: 2, background: "#bbb", borderRadius: 1 }} />)}
          </div>}
        </div>
        <div style={{ flex: 1, padding: isMini ? "3px 3px" : "8px 8px" }}>
          {!isMini && <div style={{ width: "50%", height: 3, background: "#2A7F6F", borderRadius: 1, margin: "5px 0 4px" }} />}
          {(isMini ? [90,75,60,90,70] : [90,75,60,90,75,90,60]).map((w,i) => <div key={i} style={{ width: `${w}%`, height: isMini ? 1 : 2, background: "#ddd", borderRadius: 1, marginBottom: isMini ? 2 : 3 }} />)}
          {!isMini && <>
            <div style={{ width: "40%", height: 3, background: "#2A7F6F", borderRadius: 1, margin: "5px 0 4px" }} />
            {[90,78,90,65].map((w,i) => <div key={i} style={{ width: `${w}%`, height: 2, background: "#ddd", borderRadius: 1, marginBottom: 3 }} />)}
          </>}
        </div>
      </div>
    );
  }

  // Wireframe blocks — ats
  return (
    <div style={{ width: s.w, height: s.h, aspectRatio: isMini ? undefined : "8.5/11", overflow: "hidden", borderRadius: isMini ? 4 : 6, background: "#fff", border: `1px solid rgba(123,191,179,0.15)`, padding: isMini ? "4px 3px" : "10px 8px", flexShrink: 0 }}>
      <div style={{ width: "45%", height: isMini ? 3 : 5, background: "#1a1a1a", borderRadius: 1, marginBottom: isMini ? 3 : 4 }} />
      {(isMini ? [55,50] : [55,50]).map((w,i) => <div key={i} style={{ width: `${w}%`, height: isMini ? 1 : 2, background: "#ccc", borderRadius: 1, marginBottom: isMini ? 2 : 3 }} />)}
      <div style={{ height: isMini ? 3 : 6 }} />
      <div style={{ width: "30%", height: isMini ? 2 : 3, background: "#333", borderRadius: 1, marginBottom: isMini ? 3 : 4 }} />
      {(isMini ? [88,72,55] : [88,72,55,88,72]).map((w,i) => <div key={i} style={{ width: `${w}%`, height: isMini ? 1 : 2, background: "#ccc", borderRadius: 1, marginBottom: isMini ? 2 : 3 }} />)}
      {!isMini && <>
        <div style={{ height: 6 }} />
        <div style={{ width: "30%", height: 3, background: "#333", borderRadius: 1, marginBottom: 4 }} />
        {[88,72,88,55].map((w,i) => <div key={i} style={{ width: `${w}%`, height: 2, background: "#ccc", borderRadius: 1, marginBottom: 3 }} />)}
        <div style={{ height: 6 }} />
        <div style={{ width: "30%", height: 3, background: "#333", borderRadius: 1, marginBottom: 4 }} />
        {[88,72,55].map((w,i) => <div key={i} style={{ width: `${w}%`, height: 2, background: "#ccc", borderRadius: 1, marginBottom: 3 }} />)}
      </>}
    </div>
  );
}

// ── TEMPLATE PICKER INLINE (Choose Your Style) ───────────────────────────

function TemplateChooser({ selected, onSelect, onDownload, C }) {
  const templates = [
    { id: "traditional", name: "Traditional", desc: "Navy sidebar · Two-column · Matches one-pager" },
    { id: "modern", name: "Modern", desc: "Teal accent · Full-width · Contemporary" },
    { id: "ats", name: "ATS", desc: "Plain layout · ATS-friendly · Corporate" },
  ];
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
        {templates.map(t => (
          <div key={t.id} onClick={() => onSelect(t.id)} style={{ borderRadius: 12, border: `2px solid ${selected === t.id ? "#2A7F6F" : "rgba(123,191,179,0.15)"}`, overflow: "hidden", cursor: "pointer", transition: "all 0.2s", background: selected === t.id ? "rgba(42,127,111,0.08)" : C.card, position: "relative" }}>
            {selected === t.id && <div style={{ position: "absolute", top: 6, right: 6, width: 18, height: 18, background: "#2A7F6F", color: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.55rem", fontWeight: 700, zIndex: 2 }}>✓</div>}
            <TemplateThumbnail type={t.id} size="full" C={C} />
            <div style={{ padding: "8px 9px" }}>
              <div style={{ fontSize: "0.78rem", fontWeight: 600, color: C.text, marginBottom: 1 }}>{t.name}</div>
              <div style={{ fontSize: "0.6rem", color: C.textMuted, fontWeight: 300 }}>{t.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => onDownload(selected)} style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: "#2A7F6F", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: "0.88rem", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
        ↓ Download with {templates.find(t => t.id === selected)?.name} Template
      </button>
    </div>
  );
}

// ── TEMPLATE PICKER MODAL (still used in Clarity Modal) ──────────────────

function TemplatePickerModal({ onPick, lastTemplate, onClose, C }) {
  const [selected, setSelected] = useState(lastTemplate || "traditional");
  const templates = [
    { id: "traditional", name: "Traditional", desc: "Navy sidebar · Two-column · Matches one-pager" },
    { id: "modern", name: "Modern", desc: "Teal accent · Full-width · Contemporary" },
    { id: "ats", name: "ATS", desc: "Plain layout · ATS-friendly · Corporate" },
  ];
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,18,28,0.85)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.modalBg, border: `1px solid ${C.border}`, borderRadius: 20, maxWidth: 520, width: "100%", overflow: "hidden", boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 3, background: "linear-gradient(to right,#1C2B3A,#2A7F6F,#3A9A87)" }} />
        <div style={{ padding: "22px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.1rem", color: C.text, margin: 0 }}>Choose your style</h3>
            <button onClick={onClose} style={{ background: "transparent", border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 7, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.8rem" }}>✕</button>
          </div>
          <p style={{ fontSize: "0.8rem", color: C.textMuted, margin: "0 0 18px", fontWeight: 300 }}>Your choice is remembered for next time.</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 18 }}>
            {templates.map(t => (
              <div key={t.id} onClick={() => setSelected(t.id)} style={{ borderRadius: 12, border: `2px solid ${selected === t.id ? "#2A7F6F" : "rgba(123,191,179,0.15)"}`, overflow: "hidden", cursor: "pointer", transition: "all 0.2s", background: selected === t.id ? "rgba(42,127,111,0.08)" : C.card, position: "relative" }}>
                {selected === t.id && <div style={{ position: "absolute", top: 6, right: 6, width: 18, height: 18, background: "#2A7F6F", color: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.55rem", fontWeight: 700, zIndex: 2 }}>✓</div>}
                <TemplateThumbnail type={t.id} size="full" C={C} />
                <div style={{ padding: "8px 9px" }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, color: C.text, marginBottom: 1 }}>{t.name}</div>
                  <div style={{ fontSize: "0.6rem", color: C.textMuted, fontWeight: 300 }}>{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => onPick(selected)} style={{ width: "100%", padding: "13px", borderRadius: 10, border: "none", background: "#2A7F6F", color: "#fff", fontFamily: "inherit", fontSize: "0.9rem", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
            ↓ Download with {templates.find(t => t.id === selected)?.name} Template
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CLARITY MODAL ──────────────────────────────────────────────────────────

function ClarityModal({ result, profile, onSave, onSaveLater, onClose, onViewQuickAnswers, C }) {
  const level = result.level;
  const phrase = randomFrom(CLARITY_PHRASES[level] || CLARITY_PHRASES.medium);
  const timePhrase = randomFrom(TIME_PHRASES);
  const docsPhrase = randomFrom(DOCS_READY_PHRASES);
  const [templatePickerFor, setTemplatePickerFor] = useState(null);
  const [lastTemplate, setLastTemplate] = useState(result.resumeTemplateId || result.recommendedTemplate || "traditional");

  const scoreColors = {
    high:   { circle: "#2A7F6F", text: "#3A9A87", banner: "rgba(42,127,111,0.1)", bannerBorder: "rgba(42,127,111,0.25)", bannerText: "#3A9A87", icon: "✅" },
    medium: { circle: "#B87333", text: "#C9862A", banner: "rgba(180,120,50,0.08)", bannerBorder: "rgba(180,120,50,0.22)", bannerText: "#C9862A", icon: "⚖️" },
    low:    { circle: "#C97070", text: "#C97070", banner: "rgba(180,80,80,0.07)", bannerBorder: "rgba(180,80,80,0.2)", bannerText: "#C97070", icon: "🚫" },
  };
  const sc = scoreColors[level] || scoreColors.medium;

  const decisionText = {
    high:   "Strong match. Apply with confidence — your profile speaks directly to this role.",
    medium: "Partial match. You meet the core — apply if this role genuinely interests you.",
    low:    "Low alignment. Your energy is better spent on roles that fit your profile.",
  };

  const circumference = 2 * Math.PI * 33;
  const offset = circumference - (result.score / 100) * circumference;

  const coveredCount = (result.keywords?.match(/✓ COVERED/g) || []).length > 0
    ? (result.keywords?.match(/[^\n]+/g) || []).filter(l => l.startsWith("✓")).length
    : 0;

  const handleDownload = (docType) => {
    setTemplatePickerFor(docType);
  };

  const handleTemplatePick = (template) => {
    setLastTemplate(template);
    const fname = profile?.name?.replace(/\s+/g, "_") || "Application";
    if (templatePickerFor === "resume") {
      // Bug 2 fix: persist chosen template onto result so tracker gets the same one
      if (result) result.resumeTemplateId = template;
      downloadDocx(template, "resume", result.resumeFull, "Resume.docx", profile, result.jobtitle, result.company);
    } else if (templatePickerFor === "cover") {
      // Bug 3 fix: cover letter always goes through cover path, never resume path
      downloadDocx(template, "cover", result.cover, "CoverLetter.docx", profile, result.jobtitle, result.company);
    } else {
      downloadText(`FollowUp_${result.company || fname}.txt`, result.followup);
    }
    setTemplatePickerFor(null);
  };

  return (
    <>
      {templatePickerFor && templatePickerFor !== "followup" && (
        <TemplatePickerModal onPick={handleTemplatePick} lastTemplate={lastTemplate} onClose={() => setTemplatePickerFor(null)} C={C} />
      )}
      <div style={{ position: "fixed", inset: 0, background: "rgba(10,18,28,0.82)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(3px)" }}>
        <div style={{ background: C.modalBg, border: `1px solid ${C.border}`, borderRadius: 20, maxWidth: 520, width: "100%", boxShadow: "0 24px 80px rgba(0,0,0,0.6)", overflow: "hidden", animation: "modalIn 0.35s cubic-bezier(0.34,1.56,0.64,1)", maxHeight: "92vh", overflowY: "auto" }}>
          <div style={{ height: 3, background: "linear-gradient(to right,#1C2B3A,#2A7F6F,#3A9A87)" }} />
          <div style={{ padding: "22px 24px 20px" }}>

            {/* Close */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontSize: "0.8rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>✕</button>
            </div>

            {/* Score + Phrase */}
            <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 16 }}>
              <div style={{ position: "relative", width: 80, height: 80, flexShrink: 0 }}>
                <svg width="80" height="80" viewBox="0 0 80 80" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="40" cy="40" r="33" fill="none" stroke={`${sc.circle}22`} strokeWidth="5" />
                  <circle cx="40" cy="40" r="33" fill="none" stroke={sc.circle} strokeWidth="5" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 0.8s ease" }} />
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Fraunces',serif", fontSize: "1.5rem", fontWeight: 700, color: sc.text }}>{result.score}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: "1.15rem", lineHeight: 1.2, marginBottom: 6, color: C.text }}>You're a {result.score}% match for this role.</div>
                <div style={{ fontSize: "0.83rem", color: C.textSub, lineHeight: 1.6, fontWeight: 300 }}>{phrase}</div>
              </div>
            </div>

            {result.recommendedTemplate && (
              <div style={{
                background: "rgba(42,127,111,0.08)",
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                padding: "10px 14px",
                marginBottom: 14
              }}>
                <div style={{ fontSize: "0.82rem", fontWeight: 700, color: C.tealBright, lineHeight: 1.5 }}>
                  ◈ {formatTemplateLabel(result.recommendedTemplate)} recommended for this role — {result.recommendedReason}
                </div>
                {result.recommendedConfidence && (
                  <div style={{ fontSize: "0.67rem", color: C.textMuted, marginTop: 3, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Confidence: {result.recommendedConfidence}
                  </div>
                )}
              </div>
            )}

            {/* Decision banner */}
            <div style={{ background: sc.banner, border: `1px solid ${sc.bannerBorder}`, borderRadius: 10, padding: "11px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>{sc.icon}</span>
              <span style={{ fontSize: "0.85rem", fontWeight: 600, color: sc.bannerText, lineHeight: 1.4 }}>{decisionText[level]}</span>
            </div>

            {/* Stats row */}
            <div style={{ display: "flex", gap: 9, marginBottom: 16 }}>
              {[
                { icon: "⏱", value: "45 min saved", label: "Today" },
                { icon: "◈", value: `${coveredCount > 0 ? coveredCount : "Your"} keywords`, label: "Get Seen Quicker" },
                { icon: "✦", value: "3 docs ready", label: "To download" },
              ].map((s, i) => (
                <div key={i} style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 9, padding: "9px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: "0.85rem", marginBottom: 3 }}>{s.icon}</div>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.tealBright, marginBottom: 2 }}>{s.value}</div>
                  <div style={{ fontSize: "0.62rem", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</div>
                </div>
              ))}
            </div>

            <div style={{ height: 1, background: C.border, marginBottom: 14 }} />

            {/* Documents */}
            <div style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: C.teal, marginBottom: 9 }}>{docsPhrase}</div>

            {[
              { id: "resume", icon: "◈", name: "Tailored Resume", sub: "One page · Built from your profile · Tailored to this role", hasContent: !!result.resumeFull, templateType: lastTemplate },
              { id: "cover", icon: "■", name: "Cover Letter", sub: "Under 250 words · Professional · Ready to send", hasContent: !!result.cover, templateType: "modern" },
              { id: "followup", icon: "◎", name: "Follow-Up Email", sub: "Send 5–7 days after applying · Under 100 words", hasContent: !!result.followup, templateType: null },
            ].filter(d => d.hasContent).map(doc => (
              <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", background: C.card, border: `1px solid ${C.border}`, borderRadius: 9, marginBottom: 7, transition: "border-color 0.2s" }}>
                {doc.templateType ? (
                  <TemplateThumbnail type={doc.templateType} size="mini" C={C} />
                ) : (
                  <div style={{ width: 40, height: 52, borderRadius: 4, background: "#fff", border: "1px solid rgba(123,191,179,0.15)", padding: "5px 4px", flexShrink: 0 }}>
                    <div style={{ width: "70%", height: 2, background: "#333", borderRadius: 1, marginBottom: 4 }} />
                    {[90,80,60,90,70,50].map((w,i) => <div key={i} style={{ width: `${w}%`, height: 1, background: "#ddd", borderRadius: 1, marginBottom: 2 }} />)}
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "0.83rem", fontWeight: 600, color: C.text, marginBottom: 1 }}>{doc.name}</div>
                  <div style={{ fontSize: "0.67rem", color: C.textMuted }}>{doc.sub}</div>
                </div>
                <button onClick={() => handleDownload(doc.id)} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${C.border}`, background: "transparent", color: C.tealBright, fontFamily: "inherit", fontSize: "0.72rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>↓ Download</button>
              </div>
            ))}

            {/* Quick Answers nudge */}
            <button onClick={onViewQuickAnswers} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", background: `rgba(42,127,111,0.07)`, border: `1px solid rgba(42,127,111,0.18)`, borderRadius: 9, marginBottom: 14, cursor: "pointer", width: "100%", textAlign: "left", fontFamily: "inherit", transition: "all 0.2s" }}>
              <span style={{ fontSize: "0.95rem", flexShrink: 0 }}>▸</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "0.8rem", fontWeight: 600, color: C.text, marginBottom: 1 }}>Application form questions? Already answered.</div>
                <div style={{ fontSize: "0.68rem", color: C.textMuted, fontWeight: 300 }}>Pre-written answers to rate, availability, environment, and more — ready to copy.</div>
              </div>
              <span style={{ fontSize: "0.75rem", color: C.tealBright, flexShrink: 0 }}>→</span>
            </button>

            {/* CTAs */}
            <div style={{ display: "flex", gap: 9 }}>
              <button onClick={() => onSave("applied")} style={{ flex: 1, padding: "12px 16px", borderRadius: 10, border: "none", background: C.teal, color: "#fff", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>✓ Applied — Save to Tracker</button>
              <button onClick={() => onSave("saved")} style={{ padding: "12px 16px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent", color: C.textSub, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>◈ Save for Later</button>
            </div>

            {/* Time whisper */}
            <div style={{ textAlign: "right", marginTop: 10, fontSize: "0.68rem", color: C.textMuted, fontStyle: "italic" }}>{timePhrase}</div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── LOADING MODAL ──────────────────────────────────────────────────────────

function LoadingModal({ C, darkMode }) {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setPhraseIdx(i => (i + 1) % LOADING_PHRASES.length);
        setFade(true);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(10,18,28,0.88)", zIndex: 250, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" }}>
      <div style={{ background: C.modalBg, border: `1px solid ${C.border}`, borderRadius: 20, maxWidth: 400, width: "100%", padding: "36px 32px", textAlign: "center", boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
          {darkMode ? <LogoInk height={34} /> : <LogoWarmWhite height={34} />}
        </div>
        <div style={{ width: 44, height: 44, border: `3px solid rgba(42,127,111,0.12)`, borderTopColor: C.teal, borderRadius: "50%", animation: "spin 0.9s linear infinite", margin: "0 auto 24px" }} />
        <div style={{ fontFamily: "'Fraunces',serif", fontSize: "1.05rem", color: C.text, lineHeight: 1.5, marginBottom: 10, minHeight: 52, transition: "opacity 0.4s", opacity: fade ? 1 : 0 }}>
          {LOADING_PHRASES[phraseIdx]}
        </div>
        <div style={{ fontSize: "0.75rem", color: C.textMuted, fontWeight: 300 }}>Building your complete application package…</div>
      </div>
    </div>
  );
}

// ── RESULT SECTION ─────────────────────────────────────────────────────────

function ResultSection({
  title,
  content,
  showDocx,
  docxFilename,
  profile,
  lastTemplate,
  recommendedTemplate,
  recommendedReason,
  onTemplateUsed,
  docType,
  C
}) {
  const [selected, setSelected] = useState(recommendedTemplate || lastTemplate || "traditional");
  const [expanded, setExpanded] = useState(false);
  const [editedContent, setEditedContent] = useState(content);

  useEffect(() => {
    setSelected(recommendedTemplate || lastTemplate || "traditional");
  }, [recommendedTemplate, lastTemplate]);

  const activeContent = editedContent;

  const handleDownload = (template) => {
    onTemplateUsed(template);
    // Bug 3 fix: route by docType — cover letter bypasses resume parser
    const type = docType || (docxFilename?.toLowerCase().includes("cover") ? "cover" : "resume");
    downloadDocx(template, type, activeContent, docxFilename, profile, profile._jobTitle, profile._company);
  };

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h4 style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: C.teal, margin: 0 }}>{title}</h4>
        <CopyButton text={activeContent} C={C} />
      </div>

      {showDocx && (
        <div style={{ marginBottom: 14 }}>
          {recommendedTemplate && (
            <div style={{
              background: "rgba(42,127,111,0.08)",
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: "10px 12px",
              marginBottom: 10
            }}>
              <div style={{ fontSize: "0.76rem", fontWeight: 700, color: C.tealBright, marginBottom: 3 }}>
                Recommended: {formatTemplateLabel(recommendedTemplate)}
              </div>
              <div style={{ fontSize: "0.68rem", color: C.textMuted, lineHeight: 1.5 }}>
                {recommendedReason || "Best match for this job posting."}
              </div>
            </div>
          )}
          <div style={{ fontSize: "0.8rem", fontWeight: 600, color: C.text, marginBottom: 3 }}>Choose Your Style:</div>
          <div style={{ fontSize: "0.7rem", color: C.textMuted, marginBottom: 12, fontWeight: 300 }}>Click a template, then download. Your choice is remembered.</div>
          <TemplateChooser selected={selected} onSelect={setSelected} onDownload={handleDownload} C={C} />
        </div>
      )}

      <button onClick={() => setExpanded(!expanded)} style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: `1px solid ${C.border}`, borderRadius: expanded ? "9px 9px 0 0" : 9, padding: "8px 14px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", color: C.textMuted, fontWeight: 500, transition: "all 0.2s", width: "100%" }}>
        <span style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s", fontSize: "0.7rem" }}>▶</span>
        {expanded ? "Hide & close editor" : "View & edit text"}
      </button>
      {expanded && (
        <div style={{ position: "relative" }}>
          <textarea
            value={editedContent}
            onChange={e => setEditedContent(e.target.value)}
            style={{ width: "100%", background: C.card, border: `1px solid ${C.border}`, borderRadius: "0 0 12px 12px", borderTop: "none", padding: "18px 20px", fontSize: "0.85rem", lineHeight: 1.9, color: C.textSub, whiteSpace: "pre-wrap", fontFamily: "'Courier New', monospace", minHeight: 320, resize: "vertical", boxSizing: "border-box", outline: "none" }}
          />
          {editedContent !== content && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={() => setEditedContent(content)} style={{ padding: "6px 14px", borderRadius: 7, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontFamily: "inherit", fontSize: "0.75rem", cursor: "pointer" }}>↺ Reset</button>
              <span style={{ fontSize: "0.72rem", color: C.textMuted, alignSelf: "center" }}>Edited — download will use your updated version</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── CHECKBOX GRID ──────────────────────────────────────────────────────────

function CheckGrid({ items, selected, onToggle, columns = 2, maxAllowed = 10, C }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 8 }}>
      {items.map(item => {
        const checked = selected.includes(item);
        const atMax = selected.length >= maxAllowed && !checked;
        return (
          <button key={item} onClick={() => !atMax && onToggle(item)} style={{
            display: "flex", alignItems: "center", gap: 9, padding: "10px 13px",
            borderRadius: 10, border: `1px solid ${checked ? C.teal : C.border}`,
            background: checked ? `rgba(42,127,111,0.12)` : atMax ? "rgba(255,255,255,0.01)" : C.card,
            color: checked ? C.text : atMax ? C.textMuted : C.textSub,
            fontFamily: "inherit", fontSize: "0.85rem", cursor: atMax ? "not-allowed" : "pointer",
            transition: "all 0.18s", textAlign: "left", opacity: atMax ? 0.5 : 1,
          }}>
            <span style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${checked ? C.teal : C.border}`, background: checked ? C.teal : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "0.65rem", color: "#fff" }}>
              {checked ? "✓" : ""}
            </span>
            <span style={{ flex: 1 }}>{item}</span>
          </button>
        );
      })}
    </div>
  );
}

function ChipSelector({ items, selected, onToggle, C }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {items.map(item => {
        const active = selected.includes(item);
        return (
          <button key={item} onClick={() => onToggle(item)} style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${active ? C.teal : C.border}`, background: active ? `rgba(42,127,111,0.12)` : "transparent", color: active ? C.text : C.textSub, fontFamily: "inherit", fontSize: "0.83rem", cursor: "pointer", transition: "all 0.18s" }}>
            {item}
          </button>
        );
      })}
    </div>
  );
}

// ── TOOLTIP ────────────────────────────────────────────────────────────────

function Tooltip({ text, children, C }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-flex" }} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", background: "#1C2B3A", border: `1px solid rgba(123,191,179,0.2)`, borderRadius: 8, padding: "7px 12px", fontSize: "0.75rem", color: "#F8F5F3", whiteSpace: "nowrap", zIndex: 100, lineHeight: 1.5, maxWidth: 260, textAlign: "center", boxShadow: "0 4px 20px rgba(0,0,0,0.4)", pointerEvents: "none" }}>
          {text}
        </div>
      )}
    </div>
  );
}

// ── AI ASSIST BUTTON ───────────────────────────────────────────────────────

function AIAssistButton({ label, onAssist, loading, disabled, C, prominent }) {
  return (
    <button onClick={onAssist} disabled={loading || disabled} style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      padding: prominent ? "11px 20px" : "8px 16px",
      borderRadius: prominent ? 10 : 8,
      border: `1px solid ${prominent ? C.teal : "rgba(42,127,111,0.4)"}`,
      background: prominent ? C.teal : "rgba(42,127,111,0.1)",
      color: loading ? C.textMuted : prominent ? "#fff" : C.tealBright,
      fontFamily: "inherit",
      fontSize: prominent ? "0.9rem" : "0.8rem",
      fontWeight: 700,
      cursor: loading || disabled ? "not-allowed" : "pointer",
      transition: "all 0.2s",
      opacity: disabled ? 0.5 : 1,
      animation: prominent && !loading && !disabled ? "pulse 2s infinite" : "none",
      boxShadow: prominent && !loading && !disabled ? "0 0 0 0 rgba(42,127,111,0.4)" : "none",
    }}>
      {loading ? "✦ Thinking…" : `✦ ${label}`}
    </button>
  );
}

// ── DOCUMENT MODAL ─────────────────────────────────────────────────────────

function DocModal({ app, onClose, profile, C }) {
  const [activeDoc, setActiveDoc] = useState("resume");
  const [showPicker, setShowPicker] = useState(false);
  // Bug 2 fix: seed from saved resumeTemplateId so tracker matches original download
  const [lastTemplate, setLastTemplate] = useState(app.resumeTemplateId || "traditional");

  const docs = [
    { id: "resume", label: "Resume", icon: "◈", content: app.resumeFull, filename: "Resume.docx" },
    { id: "cover", label: "Cover Letter", icon: "■", content: app.cover, filename: "CoverLetter.docx" },
    { id: "followup", label: "Follow-Up Email", icon: "◎", content: app.followup, filename: `FollowUp_${app.company || "Application"}.txt` },
    { id: "keywords", label: "Get Seen Keywords", icon: "✦", content: app.keywords, filename: `Keywords_${app.company || "Application"}.txt` },
    { id: "notes", label: "Key Notes", icon: "⚠", content: app.keyNotes, filename: `Notes_${app.company || "Application"}.txt` },
    { id: "jd", label: "Job Description", icon: "▸", content: app.jobdesc, filename: `JobDescription_${app.company || "Application"}.txt` },
  ].filter(d => d.content);

  const fitColors = {
    high:   { text: "#3A9A87" },
    medium: { text: "#f39c12" },
    low:    { text: "#c97070" },
  };

  const statusConfig = {
    saved: { color: "#8FA3B2", label: "Saved" },
    applied: { color: "#3A9A87", label: "Applied" },
    interview: { color: "#f39c12", label: "Interview" },
    offer: { color: "#7BBFB3", label: "Offer" },
    declined: { color: "#c97070", label: "Declined" },
    followup: { color: "#2A7F6F", label: "Follow-Up" },
  };

  const current = docs.find(d => d.id === activeDoc) || docs[0];

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,18,28,0.92)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      {showPicker && (
        <TemplatePickerModal
          onPick={(t) => {
            setLastTemplate(t);
            // Bug 3 fix: route by doc type — cover letter never goes through resume parser
            // Bug 2 fix: cover letter path skips template picker entirely; resume reads saved template
            const docType = current.id === "cover" ? "cover" : "resume";
            downloadDocx(t, docType, current.content, current.filename, profile, app.jobtitle, app.company);
            setShowPicker(false);
          }}
          lastTemplate={lastTemplate}
          onClose={() => setShowPicker(false)}
          C={C}
        />
      )}
      <div onClick={e => e.stopPropagation()} style={{ background: C.modalBg, border: `1px solid ${C.border}`, borderRadius: 20, maxWidth: 800, width: "100%", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px 0", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.1rem", margin: "0 0 3px", color: C.text }}>{app.jobtitle} {app.company ? `— ${app.company}` : ""}</h3>
              <p style={{ fontSize: "0.73rem", color: C.textMuted, margin: 0 }}>
                {new Date(app.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                &nbsp;·&nbsp; Fit: <span style={{ color: fitColors[app.level || "medium"].text }}>{app.score}</span>
                &nbsp;·&nbsp; <span style={{ color: statusConfig[app.status]?.color }}>{statusConfig[app.status]?.label}</span>
                {app.jobLink && <>&nbsp;·&nbsp;<a href={app.jobLink} target="_blank" rel="noopener noreferrer" style={{ color: C.teal, textDecoration: "none" }}>↗ Job Posting</a></>}
              </p>
            </div>
            <button onClick={onClose} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${C.border}`, background: "transparent", color: C.textSub, fontFamily: "inherit", cursor: "pointer", fontSize: "0.82rem" }}>Close</button>
          </div>
          {app.realityFlags && (
            <div style={{ background: "rgba(180,80,80,0.07)", border: "1px solid rgba(180,80,80,0.2)", borderRadius: 8, padding: "9px 13px", marginBottom: 12 }}>
              <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#c97070" }}>🚩 Reality Flags Detected</span>
              <div style={{ fontSize: "0.83rem", color: C.textSub, whiteSpace: "pre-wrap", lineHeight: 1.7, marginTop: 5 }}>{app.realityFlags}</div>
            </div>
          )}
          <div style={{ display: "flex", gap: 2, overflowX: "auto" }}>
            {docs.map(d => (
              <button key={d.id} onClick={() => setActiveDoc(d.id)} style={{ padding: "8px 14px", border: "none", background: "transparent", borderBottom: `2px solid ${activeDoc === d.id ? C.teal : "transparent"}`, color: activeDoc === d.id ? C.text : C.textMuted, fontFamily: "inherit", fontSize: "0.8rem", fontWeight: activeDoc === d.id ? 600 : 400, cursor: "pointer", whiteSpace: "nowrap" }}>{d.icon} {d.label}</button>
            ))}
          </div>
        </div>
        {current && (
          <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 22px", flexShrink: 0 }}>
              <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal }}>{current.label}</span>
              <div style={{ display: "flex", gap: 7 }}>
                <CopyButton text={current.content} small C={C} />
                {(current.id === "resume" || current.id === "cover") && (
                  <button onClick={() => setShowPicker(true)} style={{ background: `rgba(42,127,111,0.12)`, border: `1px solid rgba(42,127,111,0.3)`, color: C.tealBright, padding: "5px 12px", borderRadius: 7, fontSize: "0.72rem", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>↓ Download .docx</button>
                )}
                {current.id !== "resume" && current.id !== "cover" && (
                  <button onClick={() => downloadText(current.filename, current.content)} style={{ background: `rgba(42,127,111,0.08)`, border: `1px solid ${C.border}`, color: C.textSub, padding: "5px 12px", borderRadius: 7, fontSize: "0.72rem", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>↓ Download</button>
                )}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "0 22px 22px" }}>
              <div
                key={current.id}
                style={{
                  width: "100%",
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: "16px 18px",
                  fontSize: current.id === "jd" ? "0.82rem" : "0.87rem",
                  lineHeight: 1.9,
                  color: C.textSub,
                  whiteSpace: "pre-wrap",
                  fontFamily: current.id === "resume" ? "'Courier New', monospace" : "inherit",
                  minHeight: 320,
                }}
              >
                {current.content}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ONBOARDING ─────────────────────────────────────────────────────────────

function Onboarding({ onComplete, existing, C, darkMode }) {
  const [step, setStep] = useState(1);
  const [aiLoading, setAiLoading] = useState(false);
  const [resumeWarning, setResumeWarning] = useState("");
  const [certSuggestions, setCertSuggestions] = useState([]);

  const [profile, setProfile] = useState(existing || {
    name: "", city: "", timezone: "", phone: "", email: "", linkedin: "",
    education: [], certifications: "", achievements: "",
    roles: [], environment: "",
    rate: "", hoursMin: "", hoursMax: "", workType: "Open to both", location: "Remote only",
    resume: "", coreSkills: [], tools: [], workingStyle: [],
  });

  const up = (key, val) => setProfile(p => ({ ...p, [key]: val }));
  const toggleArr = (key, val) => setProfile(p => ({ ...p, [key]: p[key].includes(val) ? p[key].filter(v => v !== val) : [...p[key], val] }));
  const toggleRole = (id) => {
    const newRoles = profile.roles.includes(id) ? profile.roles.filter(r => r !== id) : [...profile.roles, id];
    up("roles", newRoles);
    setCertSuggestions(getCertsForRoles(newRoles));
  };

  const addEducation = () => up("education", [...(profile.education || []), { degree: "", institution: "", year: "", gpa: "" }]);
  const updateEdu = (i, field, val) => { const e = [...profile.education]; e[i] = { ...e[i], [field]: val }; up("education", e); };
  const removeEdu = (i) => up("education", profile.education.filter((_, idx) => idx !== i));

  const availableSkills = getSkillsForRoles(profile.roles.length ? profile.roles : ["va", "admin", "ops"]);
  const availableTools = getToolsForRoles(profile.roles.length ? profile.roles : ["va", "admin", "ops"]);
  const TOTAL_STEPS = 8;

  const inputStyle = { width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: "inherit", fontSize: "0.95rem", padding: "13px 15px", outline: "none", transition: "border-color 0.2s" };

  const handleResumeChange = (val) => {
    up("resume", val);
    if (val.length > 100) { const w = validateResume(val); setResumeWarning(w || ""); }
    else setResumeWarning("");
  };

  const findAchievements = async () => {
    if (!profile.resume || profile.resume.trim().length < 50) return;
    setAiLoading(true);
    try {
      const res = await fetch("/api/generate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 500, messages: [{ role: "user", content: `Read this resume and identify 1-2 standout achievements — specific things with numbers, budgets, team sizes, or measurable impact. Write each as one sentence in first person. Be specific. Only use what's actually in the resume.\n\nRESUME:\n${profile.resume}\n\nRespond with just the 1-2 achievement sentences, nothing else.` }] }) });
      const data = await res.json();
      const text = data.content?.map(i => i.text || "").join("") || "";
      if (text) up("achievements", text.trim());
    } catch (e) {}
    setAiLoading(false);
  };

  const aiPickSkills = async () => {
    if (profile.coreSkills.length === 0) return;
    setAiLoading(true);
    try {
      const res = await fetch("/api/generate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 300, messages: [{ role: "user", content: `Based on this resume and the skills already selected, pick the most relevant additional skills to reach a total of 10. Return ONLY a comma-separated list of skill names from the available list — no explanation.\n\nResume: ${profile.resume || "Not provided"}\nAlready selected: ${profile.coreSkills.join(", ")}\nAvailable skills: ${availableSkills.join(", ")}\nTarget roles: ${profile.roles.join(", ")}\n\nReturn only the NEW skills to add, comma-separated.` }] }) });
      const data = await res.json();
      const text = data.content?.map(i => i.text || "").join("") || "";
      const newSkills = text.split(",").map(s => s.trim()).filter(s => availableSkills.includes(s) && !profile.coreSkills.includes(s));
      up("coreSkills", [...profile.coreSkills, ...newSkills].slice(0, 10));
    } catch (e) {}
    setAiLoading(false);
  };

  const aiPickTools = async () => {
    if (profile.tools.length === 0) return;
    setAiLoading(true);
    try {
      const res = await fetch("/api/generate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 300, messages: [{ role: "user", content: `Based on this resume and tools already selected, pick the most relevant additional tools to reach a total of 10. Return ONLY a comma-separated list of tool names from the available list — no explanation.\n\nResume: ${profile.resume || "Not provided"}\nAlready selected: ${profile.tools.join(", ")}\nAvailable tools: ${availableTools.join(", ")}\nTarget roles: ${profile.roles.join(", ")}\n\nReturn only the NEW tools to add, comma-separated.` }] }) });
      const data = await res.json();
      const text = data.content?.map(i => i.text || "").join("") || "";
      const newTools = text.split(",").map(s => s.trim()).filter(s => availableTools.includes(s) && !profile.tools.includes(s));
      up("tools", [...profile.tools, ...newTools].slice(0, 10));
    } catch (e) {}
    setAiLoading(false);
  };

  const canNext = () => {
    if (step === 1) return profile.roles.length > 0;
    if (step === 2) return profile.environment !== "";
    if (step === 3) return profile.rate && profile.hoursMin && profile.hoursMax;
    if (step === 4) return profile.resume.trim().length > 50 && !resumeWarning;
    if (step === 5) return profile.name.trim().length > 0 && profile.email.trim().length > 0;
    if (step === 6) return profile.coreSkills.length >= 3;
    if (step === 7) return profile.tools.length >= 3;
    return true;
  };

  const stepLabels = ["Role", "Environment", "Basics", "Resume", "Profile", "Skills", "Tools", "Style"];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        input::placeholder, textarea::placeholder { color: ${C.textMuted}; }
        select option { background: ${C.bg}; }
        input:focus, textarea:focus, select:focus { border-color: ${C.teal} !important; }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 0 rgba(42,127,111,0.4)} 50%{box-shadow:0 0 0 8px rgba(42,127,111,0)} }
      `}</style>
      <div style={{ maxWidth: 620, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
            {darkMode ? <LogoInk height={38} /> : <LogoWarmWhite height={38} />}
          </div>
          <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.6rem", color: C.text, lineHeight: 1.25, margin: "0 0 8px" }}>Let's set up your command center.</h1>
          <p style={{ color: C.textSub, fontSize: "0.88rem", margin: 0, fontWeight: 300 }}>About 5 minutes. Everything personalizes to you after this.</p>
        </div>

        <div style={{ display: "flex", gap: 4, marginBottom: 28 }}>
          {stepLabels.map((label, i) => (
            <div key={i} style={{ flex: 1, textAlign: "center" }}>
              <div style={{ height: 3, borderRadius: 99, background: i + 1 <= step ? C.teal : "rgba(123,191,179,0.1)", transition: "background 0.3s", marginBottom: 4 }} />
              <div style={{ fontSize: "0.58rem", textTransform: "uppercase", letterSpacing: "0.06em", color: i + 1 === step ? C.tealBright : C.textMuted }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: "28px 24px" }}>

          {step === 1 && (
            <div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.25rem", color: C.text, margin: "0 0 6px" }}>What kind of work are you looking for?</h2>
              <p style={{ color: C.textSub, fontSize: "0.85rem", margin: "0 0 20px", fontWeight: 300 }}>Select all that apply.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {ROLE_OPTIONS.map(r => (
                  <button key={r.id} onClick={() => toggleRole(r.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", borderRadius: 12, border: `1px solid ${profile.roles.includes(r.id) ? C.teal : C.border}`, background: profile.roles.includes(r.id) ? "rgba(42,127,111,0.12)" : C.card, color: profile.roles.includes(r.id) ? C.text : C.textSub, fontFamily: "inherit", fontSize: "0.9rem", cursor: "pointer", transition: "all 0.18s", textAlign: "left" }}>
                    <span style={{ color: C.teal, flexShrink: 0 }}>{r.icon}</span>
                    <span style={{ flex: 1 }}>{r.label}</span>
                    {profile.roles.includes(r.id) && <span style={{ color: C.teal, fontSize: "0.75rem" }}>✓</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.25rem", color: C.text, margin: "0 0 6px" }}>What kind of environment do you work best in?</h2>
              <p style={{ color: C.textSub, fontSize: "0.85rem", margin: "0 0 20px", fontWeight: 300 }}>Choose the one that fits you best.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {ENVIRONMENT_OPTIONS.map(e => (
                  <button key={e.id} onClick={() => up("environment", e.id)} style={{ display: "flex", alignItems: "flex-start", gap: 13, padding: "16px", borderRadius: 12, border: `1px solid ${profile.environment === e.id ? C.teal : C.border}`, background: profile.environment === e.id ? "rgba(42,127,111,0.12)" : C.card, color: C.text, fontFamily: "inherit", cursor: "pointer", transition: "all 0.18s", textAlign: "left" }}>
                    <span style={{ color: C.teal, fontSize: "1rem", flexShrink: 0, marginTop: 1 }}>{e.icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "0.92rem", marginBottom: 5, color: profile.environment === e.id ? C.text : C.textSub }}>{e.label}</div>
                      <div style={{ fontSize: "0.82rem", color: C.textMuted, lineHeight: 1.65, fontWeight: 300 }}>{e.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.25rem", color: C.text, margin: "0 0 6px" }}>Tell us your work basics.</h2>
              <p style={{ color: C.textSub, fontSize: "0.85rem", margin: "0 0 20px", fontWeight: 300 }}>Every job gets scored against these numbers.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div>
                  <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 8 }}>Minimum Hourly Rate</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: C.textSub, fontSize: "1.1rem" }}>$</span>
                    <input type="number" value={profile.rate} onChange={e => up("rate", e.target.value)} placeholder="25" style={{ ...inputStyle, width: 100 }} />
                    <span style={{ color: C.textMuted, fontSize: "0.85rem" }}>/hour minimum</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 8 }}>Hours Per Week</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="number" value={profile.hoursMin} onChange={e => up("hoursMin", e.target.value)} placeholder="20" style={{ ...inputStyle, width: 80 }} />
                    <span style={{ color: C.textMuted }}>–</span>
                    <input type="number" value={profile.hoursMax} onChange={e => up("hoursMax", e.target.value)} placeholder="40" style={{ ...inputStyle, width: 80 }} />
                    <span style={{ color: C.textMuted, fontSize: "0.85rem" }}>hours/week</span>
                  </div>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 8 }}>Work Type</label>
                  <select value={profile.workType} onChange={e => up("workType", e.target.value)} style={{ ...inputStyle }}>
                    {WORK_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 8 }}>Location Preference</label>
                  <select value={profile.location} onChange={e => up("location", e.target.value)} style={{ ...inputStyle }}>
                    {LOCATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.25rem", color: C.text, margin: "0 0 6px" }}>Paste your resume here.</h2>
              <p style={{ color: C.textSub, fontSize: "0.85rem", margin: "0 0 16px", fontWeight: 300 }}>Paste your full resume only — company names, job titles, dates, and bullet points for each role. Do not paste a one-pager, bio, or cover letter here.</p>
              <textarea value={profile.resume} onChange={e => handleResumeChange(e.target.value)} rows={12} placeholder="Paste your full resume here — job title, company, dates, and bullets for each role…" style={{ ...inputStyle, resize: "vertical", lineHeight: 1.65 }} />
              {resumeWarning && (
                <div style={{ marginTop: 10, padding: "12px 14px", background: "rgba(243,156,18,0.08)", border: "1px solid rgba(243,156,18,0.25)", borderRadius: 8, fontSize: "0.82rem", color: "#f39c12", lineHeight: 1.6 }}>
                  <div>⚠ {resumeWarning}</div>
                  <div style={{ marginTop: 6, color: "rgba(243,156,18,0.7)", fontSize: "0.78rem" }}>Each role should have: job title · company name · dates · 2–4 bullet points</div>
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.25rem", color: C.text, margin: "0 0 6px" }}>Tell us about yourself.</h2>
              <p style={{ color: C.textSub, fontSize: "0.85rem", margin: "0 0 20px", fontWeight: 300 }}>This goes on every resume and cover letter we generate.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 7 }}>Full Name *</label>
                    <input value={profile.name} onChange={e => up("name", e.target.value)} placeholder="Your full name" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 7 }}>City / State</label>
                    <input value={profile.city} onChange={e => up("city", e.target.value)} placeholder="e.g. Atlanta, GA" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 7 }}>Email *</label>
                    <input type="email" value={profile.email} onChange={e => up("email", e.target.value)} placeholder="your@email.com" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 7 }}>Phone</label>
                    <input value={profile.phone} onChange={e => up("phone", formatPhone(e.target.value))} placeholder="(000) 000-0000" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 7 }}>Timezone</label>
                    <select value={profile.timezone} onChange={e => up("timezone", e.target.value)} style={inputStyle}>
                      <option value="">Select timezone</option>
                      {TIMEZONE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 7 }}>LinkedIn (optional)</label>
                    <input value={profile.linkedin} onChange={e => up("linkedin", e.target.value)} placeholder="linkedin.com/in/yourname" style={inputStyle} />
                  </div>
                </div>

                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                    <label style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal }}>Education (optional)</label>
                    <button onClick={addEducation} style={{ fontSize: "0.74rem", color: C.teal, background: "none", border: `1px solid rgba(42,127,111,0.3)`, borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}>+ Add</button>
                  </div>
                  {(profile.education || []).map((edu, i) => (
                    <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px", marginBottom: 8 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                        <input value={edu.degree} onChange={e => updateEdu(i, "degree", e.target.value)} placeholder="Degree" style={{ ...inputStyle, fontSize: "0.85rem", padding: "9px 11px" }} />
                        <input value={edu.institution} onChange={e => updateEdu(i, "institution", e.target.value)} placeholder="Institution" style={{ ...inputStyle, fontSize: "0.85rem", padding: "9px 11px" }} />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8 }}>
                        <input value={edu.year} onChange={e => updateEdu(i, "year", e.target.value)} placeholder="Year" style={{ ...inputStyle, fontSize: "0.85rem", padding: "9px 11px" }} />
                        <input value={edu.gpa} onChange={e => updateEdu(i, "gpa", e.target.value)} placeholder="GPA (optional)" style={{ ...inputStyle, fontSize: "0.85rem", padding: "9px 11px" }} />
                        <button onClick={() => removeEdu(i)} style={{ padding: "0 12px", borderRadius: 8, border: "none", background: "rgba(255,255,255,0.04)", color: C.textMuted, cursor: "pointer" }}>✕</button>
                      </div>
                    </div>
                  ))}
                  {(profile.education || []).length === 0 && <p style={{ fontSize: "0.78rem", color: C.textMuted, margin: "4px 0 0" }}>No education added — section will be omitted from resume.</p>}
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 7 }}>Certifications & Training (optional)</label>
                  {certSuggestions.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <p style={{ fontSize: "0.77rem", color: C.textSub, margin: "0 0 8px" }}>Common for your role — click to add:</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {certSuggestions.map(cert => (
                          <button key={cert} onClick={() => { const c = profile.certifications ? profile.certifications + ", " + cert : cert; up("certifications", c); }} style={{ padding: "4px 10px", borderRadius: 20, border: `1px solid ${C.border}`, background: "transparent", color: C.textSub, fontFamily: "inherit", fontSize: "0.75rem", cursor: "pointer" }}>+ {cert}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <input value={profile.certifications || ""} onChange={e => up("certifications", e.target.value)} placeholder="e.g. Google Project Management Certificate, HubSpot CRM Certified" style={inputStyle} />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 7 }}>Notable Achievements (optional — but powerful)</label>
                  <p style={{ fontSize: "0.78rem", color: C.textMuted, lineHeight: 1.65, margin: "0 0 9px" }}>2–3 standout moments from your career — budgets managed, projects launched, things handled under pressure.</p>
                  <textarea value={profile.achievements || ""} onChange={e => up("achievements", e.target.value)} rows={3} placeholder="e.g. Managed $50K nonprofit grant — budget tracking, vendor coordination, and quarterly reporting." style={{ ...inputStyle, resize: "vertical", lineHeight: 1.65 }} />
                  {profile.resume.trim().length > 50 && (
                    <div style={{ marginTop: 10 }}>
                      <AIAssistButton label="Find my achievements" onAssist={findAchievements} loading={aiLoading} C={C} />
                      <p style={{ fontSize: "0.75rem", color: C.textMuted, margin: "7px 0 0" }}>Not sure what to write? We'll read your resume and surface 1–2 standout moments for you.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 6 && (
            <div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.25rem", color: C.text, margin: "0 0 6px" }}>Select your core skills.</h2>
              <p style={{ color: C.textSub, fontSize: "0.85rem", margin: "0 0 6px", fontWeight: 300 }}>Check everything you can genuinely do. These go directly on your resume and drive your job match score.</p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                <p style={{ fontSize: "0.75rem", color: C.teal, margin: 0 }}>Select at least 3 · max 10 · {profile.coreSkills.length} selected</p>
                {profile.coreSkills.length >= 3 && profile.coreSkills.length < 10 && (
                  <AIAssistButton label="Help me pick the rest" onAssist={aiPickSkills} loading={aiLoading} C={C} prominent={true} />
                )}
              </div>
              <CheckGrid items={availableSkills} selected={profile.coreSkills} onToggle={v => toggleArr("coreSkills", v)} columns={2} maxAllowed={10} C={C} />
              {profile.coreSkills.length >= 10 && <p style={{ fontSize: "0.77rem", color: C.teal, marginTop: 10 }}>✓ Maximum reached — looking good.</p>}
            </div>
          )}

          {step === 7 && (
            <div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.25rem", color: C.text, margin: "0 0 6px" }}>Which tools & tech do you know?</h2>
              <p style={{ color: C.textSub, fontSize: "0.85rem", margin: "0 0 6px", fontWeight: 300 }}>Specific tool names matter for job matching. Check every tool you've actually used.</p>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                <p style={{ fontSize: "0.75rem", color: C.teal, margin: 0 }}>Select at least 3 · max 10 · {profile.tools.length} selected</p>
                {profile.tools.length >= 3 && profile.tools.length < 10 && (
                  <AIAssistButton label="Help me pick the rest" onAssist={aiPickTools} loading={aiLoading} C={C} prominent={true} />
                )}
              </div>
              <CheckGrid items={availableTools} selected={profile.tools} onToggle={v => toggleArr("tools", v)} columns={2} maxAllowed={10} C={C} />
              {profile.tools.length >= 10 && <p style={{ fontSize: "0.77rem", color: C.teal, marginTop: 10 }}>✓ Maximum reached — looking good.</p>}
            </div>
          )}

          {step === 8 && (
            <div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.25rem", color: C.text, margin: "0 0 6px" }}>How do you work best?</h2>
              <p style={{ color: C.textSub, fontSize: "0.85rem", margin: "0 0 6px", fontWeight: 300 }}>These appear on your resume as your Working Style section — the thing most resumes are missing.</p>
              <p style={{ fontSize: "0.75rem", color: C.teal, margin: "0 0 16px" }}>Select all that apply · {profile.workingStyle.length} selected · optional but recommended</p>
              <ChipSelector items={WORKING_STYLE_OPTIONS} selected={profile.workingStyle} onToggle={v => toggleArr("workingStyle", v)} C={C} />
              <div style={{ marginTop: 24, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px" }}>
                <p style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, margin: "0 0 12px" }}>Your Complete Profile</p>
                {[
                  ["Name", profile.name || "—"],
                  ["Looking for", ROLE_OPTIONS.filter(r => profile.roles.includes(r.id)).map(r => r.label).join(", ") || "—"],
                  ["Environment", ENVIRONMENT_OPTIONS.find(e => e.id === profile.environment)?.label || "—"],
                  ["Rate", profile.rate ? `$${profile.rate}/hr minimum` : "—"],
                  ["Hours", profile.hoursMin && profile.hoursMax ? `${profile.hoursMin}–${profile.hoursMax} hrs/wk` : "—"],
                  ["Skills", `${profile.coreSkills.length} selected`],
                  ["Tools", `${profile.tools.length} selected`],
                  ["Resume", profile.resume.trim().length > 50 ? `✓ ${profile.resume.trim().split(/\s+/).length} words` : "—"],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", gap: 12, marginBottom: 6, fontSize: "0.85rem" }}>
                    <span style={{ color: C.textMuted, minWidth: 85, flexShrink: 0 }}>{label}</span>
                    <span style={{ color: C.textSub }}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 26 }}>
            {step > 1
              ? <button onClick={() => setStep(s => s - 1)} style={{ padding: "11px 19px", borderRadius: 9, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontFamily: "inherit", fontSize: "0.85rem", cursor: "pointer" }}>← Back</button>
              : <div />
            }
            <button onClick={() => step < TOTAL_STEPS ? setStep(s => s + 1) : onComplete(profile)} disabled={!canNext()} style={{ padding: "12px 24px", borderRadius: 9, border: "none", background: canNext() ? C.teal : "rgba(42,127,111,0.15)", color: canNext() ? "#fff" : C.textMuted, fontFamily: "inherit", fontSize: "0.9rem", fontWeight: 600, cursor: canNext() ? "pointer" : "not-allowed", transition: "all 0.2s" }}>
              {step === TOTAL_STEPS ? "Launch my command center →" : "Continue →"}
            </button>
          </div>
        </div>
        <p style={{ textAlign: "center", fontSize: "0.68rem", color: C.textMuted, marginTop: 16 }}>JobLanded · Fit First. Apply Fast. · Your data stays in your browser · Edit anytime via ⚙</p>
      </div>
    </div>
  );
}

// ── LOGO SVGs ──────────────────────────────────────────────────────────────

// Ink logo: dark navy bg, white "Job" + teal "anded" — used in dark mode & lock screen
const LogoInk = ({ height = 40 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="100 460 1250 400" style={{ height, width: "auto", display: "block" }}>
    <defs>
      <clipPath id="ink-clip1"><path d="M 847 493 L 863.167969 493 L 863.167969 512 L 847 512 Z" clipRule="nonzero"/></clipPath>
      <clipPath id="ink-clip2"><path d="M 519.667969 483.484375 L 854 483.484375 L 854 826 L 519.667969 826 Z" clipRule="nonzero"/></clipPath>
    </defs>
    {/* Bird / checkmark mark */}
    <g fill="#2a7f6f">
      <path d="M 837.042969 488.226562 L 832.410156 494.40625 C 833.410156 494.273438 838.191406 488.601562 837.042969 488.226562 Z"/>
      <g clipPath="url(#ink-clip1)"><path d="M 862.773438 493.375 L 847.390625 509.546875 L 847.847656 511.386719 C 849.960938 507.695312 852.996094 505.589844 855.828125 502.378906 C 856.007812 502.171875 855.644531 501.039062 855.828125 500.835938 C 856.253906 500.359375 865.917969 492.953125 862.773438 493.375 Z"/></g>
      <path d="M 842.703125 514.476562 L 847.390625 509.546875 L 847.332031 509.332031 Z"/>
      <path d="M 822.371094 500.578125 C 823.136719 499.65625 823.597656 497.996094 824.945312 497.234375 C 824.972656 495.746094 824.242188 497.15625 823.917969 496.976562 L 816.714844 504.4375 C 816.40625 506.707031 822.078125 500.933594 822.371094 500.578125 Z"/>
      <path d="M 763.1875 561.566406 C 766.574219 560.902344 783.371094 539.070312 785.058594 541.75 C 785.980469 540.972656 789.398438 534.554688 790.980469 536.863281 C 790.042969 537.394531 790.195312 537.996094 789.949219 538.664062 C 791.734375 536.289062 794.085938 537.421875 790.464844 534.289062 C 790.722656 531.238281 814.53125 514.304688 814.398438 507.785156 L 761.128906 562.082031 C 762.21875 562.917969 762.554688 561.96875 763.1875 561.566406 Z"/>
      <path d="M 838.070312 512.933594 L 838.585938 513.449219 C 840.195312 513.351562 838.953125 512.75 838.070312 512.933594 Z"/>
      <path d="M 845.789062 513.445312 L 840.902344 517.820312 C 841.265625 518.957031 846.484375 514.867188 845.789062 513.445312 Z"/>
      <path d="M 844.503906 521.164062 C 844.285156 523.964844 846.933594 520.492188 847.589844 520.136719 C 845.738281 518.65625 846.898438 519.742188 844.503906 521.164062 Z"/>
      <path d="M 830.867188 521.679688 C 830.488281 521.792969 829.683594 522.765625 830.351562 523.222656 C 831.132812 522.703125 832.183594 521.289062 830.867188 521.679688 Z"/>
      <g clipPath="url(#ink-clip2)"><path d="M 813.882812 563.882812 C 812.597656 564.707031 806.582031 568.027344 805.386719 566.714844 C 806.675781 564.941406 811.230469 563.621094 812.078125 562.082031 C 812.570312 561.191406 812.394531 557.476562 813.363281 557.195312 C 816.019531 561.820312 825.019531 544.4375 829.574219 543.554688 C 826.230469 543.164062 828.878906 539.75 827.257812 539.695312 C 826.835938 539.683594 822.285156 544.992188 821.335938 545.613281 L 823.910156 546.128906 L 820.058594 550.503906 L 819.542969 548.445312 L 815.425781 552.5625 C 815.503906 551.761719 814.605469 549.382812 814.652344 549.21875 C 814.902344 548.351562 827.859375 539.097656 827.003906 536.351562 C 826.96875 536.246094 824.425781 537.097656 825.457031 535.324219 C 825.625 535.042969 830.128906 535.511719 830.605469 532.238281 L 827.261719 534.554688 L 827.773438 532.496094 C 829.589844 532.230469 834.847656 526.511719 835.496094 526.316406 C 836.417969 526.046875 837.023438 528.46875 838.328125 526.574219 C 835.300781 525.015625 838.296875 524.078125 839.355469 522.199219 C 839.246094 522.242188 836.890625 522.609375 837.039062 521.683594 L 806.671875 549.992188 L 822.882812 533.265625 L 823.140625 531.464844 C 821.289062 530.609375 800.632812 554.625 798.953125 554.625 C 798.859375 554.625 797.667969 553.429688 797.667969 553.339844 C 797.667969 552.007812 815.472656 538.265625 817.226562 535.328125 L 816.96875 532.496094 L 783.257812 563.886719 C 796.078125 548.609375 812.570312 536.777344 824.945312 521.171875 L 827.003906 522.199219 C 824 525.253906 820.898438 528.425781 818.257812 531.722656 C 819.402344 533.695312 831.628906 519.648438 832.15625 518.082031 C 834.746094 519.171875 835.207031 515.488281 835.757812 513.964844 C 836.011719 513.253906 837.5625 512.40625 836.273438 511.90625 C 837.289062 510.847656 837.542969 510.585938 838.847656 509.847656 C 839.53125 509.457031 840.601562 509.40625 841.164062 508.300781 L 841.164062 510.363281 C 845.136719 506.90625 842.050781 508.480469 841.9375 507.015625 C 844.984375 503.953125 848.84375 500.902344 849.65625 496.46875 C 849.65625 496.46875 853.777344 491.578125 853.777344 491.578125 L 850.6875 493.898438 L 850.171875 493.898438 C 850.824219 492.46875 843.953125 497.113281 843.484375 497.5 C 842.558594 498.378906 837.816406 503.195312 836.796875 504.1875 C 834.199219 504.03125 837.082031 502.375 836.539062 501.101562 C 834.933594 503.101562 832.441406 504.746094 831.644531 507.277344 C 831.429688 507.527344 831.074219 507.59375 830.617188 508.308594 L 828.558594 508.566406 C 829.953125 505.878906 837.253906 495.1875 840.140625 495.960938 C 843.167969 493.074219 845.550781 487.316406 848.375 483.609375 L 827.015625 505.738281 C 798.996094 526.640625 780.839844 554.28125 759.59375 579.851562 C 758.539062 580.757812 746.980469 596.238281 748.789062 589.628906 C 735.742188 606.164062 721.589844 621.9375 708.644531 638.78125 L 706.328125 639.554688 L 706.070312 637.238281 C 702.582031 642.597656 698.046875 650.484375 693.71875 654.996094 C 692.378906 656.390625 689.914062 659.578125 689.085938 656.796875 C 688.980469 655.789062 693.996094 651.734375 694.746094 650.617188 C 695.050781 650.167969 693.714844 649.785156 694.746094 648.300781 C 703.386719 635.890625 716.167969 626.820312 724.597656 615.621094 C 727.246094 608.78125 731.964844 602.199219 737.976562 597.609375 L 734.890625 598.125 L 747.5 584.484375 C 744.195312 586.769531 739.53125 588.828125 736.949219 591.945312 C 722.574219 609.292969 707.398438 630.542969 694.234375 648.300781 C 688.546875 655.976562 684.1875 660.097656 691.660156 648.042969 C 690.964844 647.667969 687.132812 653.210938 687.027344 650.617188 C 687.949219 647.671875 701.546875 634.675781 700.921875 633.121094 C 699.777344 630.257812 693.28125 637.617188 692.175781 638.78125 C 665.765625 667.675781 646.777344 702.84375 621.40625 732.707031 C 605.394531 712.289062 588.347656 692.835938 569.425781 675.0625 C 568.066406 678.523438 568.335938 674.671875 566.59375 675.835938 C 586.164062 705.480469 554.710938 662.847656 552.183594 666.570312 L 557.070312 676.09375 C 555.296875 676.441406 547.835938 666.535156 547.035156 669.144531 C 546.953125 669.402344 549.441406 673.707031 545.75 673.007812 C 544.863281 672.835938 541.949219 667.703125 541.113281 668.371094 L 541.371094 672.75 L 539.054688 671.464844 C 535.164062 674.824219 539.132812 678.105469 538.796875 679.441406 C 537.957031 682.816406 533.894531 677.976562 533.394531 678.15625 C 532.832031 678.355469 532.21875 681.359375 532.109375 681.5 C 531.679688 682.042969 530.023438 681.34375 529.535156 682.527344 C 529.3125 683.070312 534.261719 693.867188 528.761719 686.902344 C 525.117188 691.617188 526.847656 697.125 519.753906 691.277344 C 519.179688 693.070312 521.925781 700.570312 521.8125 701.058594 C 521.644531 701.769531 519.78125 702.476562 519.753906 702.601562 C 520.214844 709.699219 528.652344 715.03125 533.652344 720.101562 C 534.8125 721.335938 536.414062 725.914062 536.738281 726.277344 C 550.214844 734.128906 561.925781 749.386719 572.25 761.277344 C 581.3125 772.050781 612.988281 818.996094 620.371094 823.292969 C 629.253906 828.464844 636.273438 825.175781 641.992188 817.632812 L 640.1875 820.976562 C 641.136719 821.625 642.214844 820.011719 642.765625 819.433594 C 659.675781 801.378906 669.238281 776.839844 684.453125 757.675781 L 682.910156 756.648438 C 694.289062 747.34375 687.808594 743.816406 693.714844 741.722656 C 693.761719 741.589844 692.359375 740.789062 692.6875 740.179688 C 693.871094 737.949219 695.074219 735.699219 696.289062 733.488281 C 696.769531 732.625 698.230469 732.144531 698.347656 731.945312 C 695.710938 729.507812 710.609375 714.683594 712.242188 710.84375 C 714.476562 705.515625 719.71875 698.375 723.054688 693.085938 C 725.09375 690.097656 728.730469 686.996094 729.488281 683.308594 L 722.542969 693.085938 C 719.078125 696.132812 721.640625 692.855469 720.222656 693.085938 C 718.6875 697.414062 706.074219 711.082031 702.984375 714.703125 C 713.175781 698.886719 723.496094 683.003906 734.375 667.613281 C 738.929688 661.507812 746.386719 655.257812 750.332031 649.601562 C 750.375 649.535156 749.351562 648.785156 749.816406 648.058594 C 751.210938 645.875 753.523438 643.996094 754.960938 640.851562 C 755.136719 640.472656 752.328125 639.019531 757.535156 638.277344 L 754.960938 635.191406 C 759.035156 629.253906 764.648438 624.558594 769.371094 619.234375 C 769.425781 619.175781 768.347656 618.261719 768.855469 617.691406 C 769.628906 616.824219 772.210938 614.316406 773.230469 613.316406 C 775.097656 612.816406 778.808594 611.445312 777.605469 608.941406 C 780.097656 606.292969 783.105469 602.902344 785.585938 600.449219 C 785.6875 600.351562 787.542969 600.773438 787.902344 599.675781 C 788.382812 598.214844 785.996094 597.992188 785.84375 597.101562 C 785.632812 595.886719 789.078125 592.925781 789.960938 591.957031 C 790.144531 591.757812 789.777344 590.609375 789.960938 590.414062 C 791.101562 589.183594 791.824219 590.644531 792.277344 590.15625 C 796.085938 586.0625 798.820312 580.21875 804.628906 580.375 C 806.25 580.421875 806.675781 582.761719 808.746094 580.890625 C 811.625 578.285156 807.675781 578.988281 807.460938 577.546875 C 807.339844 576.757812 815.363281 565.167969 816.207031 564.167969 C 819.683594 560.058594 818.152344 563.421875 819.554688 563.394531 C 821.03125 561.554688 830.355469 552.074219 830.617188 550.785156 C 830.863281 549.589844 830.484375 548.441406 829.84375 547.441406 C 824.941406 552.699219 820.246094 559.816406 813.882812 563.882812 Z"/></g>
    </g>
    {/* "Job" — warm white */}
    <g fill="#f8f6f3">
      <g transform="translate(179.941424, 822.050309)"><path d="M 51.21875 2.453125 C 37.34375 2.453125 26.222656 -1.457031 17.859375 -9.28125 C 9.492188 -17.101562 5.3125 -28.222656 5.3125 -42.640625 L 31.421875 -42.640625 C 31.554688 -35.972656 33.117188 -30.664062 36.109375 -26.71875 C 39.109375 -22.78125 43.941406 -20.8125 50.609375 -20.8125 C 56.859375 -20.8125 61.410156 -22.679688 64.265625 -26.421875 C 67.128906 -30.160156 68.5625 -35.09375 68.5625 -41.21875 L 68.5625 -142.828125 L 94.671875 -142.828125 L 94.671875 -41.21875 C 94.671875 -27.207031 90.757812 -16.425781 82.9375 -8.875 C 75.125 -1.320312 64.550781 2.453125 51.21875 2.453125 Z"/></g>
      <g transform="translate(288.497163, 822.050309)"><path d="M 61.828125 2.453125 C 52.035156 2.453125 43.226562 0.207031 35.40625 -4.28125 C 27.582031 -8.769531 21.390625 -14.992188 16.828125 -22.953125 C 12.273438 -30.910156 10 -40.128906 10 -50.609375 C 10 -61.078125 12.3125 -70.289062 16.9375 -78.25 C 21.5625 -86.207031 27.785156 -92.429688 35.609375 -96.921875 C 43.429688 -101.410156 52.238281 -103.65625 62.03125 -103.65625 C 71.6875 -103.65625 80.425781 -101.410156 88.25 -96.921875 C 96.070312 -92.429688 102.257812 -86.207031 106.8125 -78.25 C 111.375 -70.289062 113.65625 -61.078125 113.65625 -50.609375 C 113.65625 -40.128906 111.375 -30.910156 106.8125 -22.953125 C 102.257812 -14.992188 96.035156 -8.769531 88.140625 -4.28125 C 80.253906 0.207031 71.484375 2.453125 61.828125 2.453125 Z M 61.828125 -20.203125 C 68.628906 -20.203125 74.546875 -22.75 79.578125 -27.84375 C 84.609375 -32.945312 87.125 -40.535156 87.125 -50.609375 C 87.125 -60.671875 84.609375 -68.25 79.578125 -73.34375 C 74.546875 -78.445312 68.695312 -81 62.03125 -81 C 55.09375 -81 49.140625 -78.445312 44.171875 -73.34375 C 39.210938 -68.25 36.734375 -60.671875 36.734375 -50.609375 C 36.734375 -40.535156 39.210938 -32.945312 44.171875 -27.84375 C 49.140625 -22.75 55.023438 -20.203125 61.828125 -20.203125 Z"/></g>
      <g transform="translate(412.152761, 822.050309)"><path d="M 73.25 2.453125 C 65.632812 2.453125 58.96875 1.023438 53.25 -1.828125 C 47.539062 -4.691406 42.914062 -8.707031 39.375 -13.875 L 36.53125 0 L 13.265625 0 L 13.265625 -146.90625 L 39.375 -146.90625 L 39.375 -86.71875 C 42.644531 -91.207031 46.960938 -95.148438 52.328125 -98.546875 C 57.703125 -101.953125 64.675781 -103.65625 73.25 -103.65625 C 82.769531 -103.65625 91.269531 -101.34375 98.75 -96.71875 C 106.238281 -92.09375 112.160156 -85.765625 116.515625 -77.734375 C 120.867188 -69.710938 123.046875 -60.601562 123.046875 -50.40625 C 123.046875 -40.195312 120.867188 -31.113281 116.515625 -23.15625 C 112.160156 -15.195312 106.238281 -8.9375 98.75 -4.375 C 91.269531 0.175781 82.769531 2.453125 73.25 2.453125 Z M 67.75 -20.40625 C 76.039062 -20.40625 82.90625 -23.191406 88.34375 -28.765625 C 93.789062 -34.347656 96.515625 -41.5625 96.515625 -50.40625 C 96.515625 -59.238281 93.789062 -66.507812 88.34375 -72.21875 C 82.90625 -77.9375 76.039062 -80.796875 67.75 -80.796875 C 59.3125 -80.796875 52.40625 -77.972656 47.03125 -72.328125 C 41.65625 -66.679688 38.96875 -59.441406 38.96875 -50.609375 C 38.96875 -41.765625 41.65625 -34.519531 47.03125 -28.875 C 52.40625 -23.226562 59.3125 -20.40625 67.75 -20.40625 Z"/></g>
    </g>
    <g fill="#2a7f6f">
      <g transform="translate(685.67817, 822.044909)"><path d="M 48.140625 2.453125 C 39.441406 2.453125 32.300781 1.054688 26.71875 -1.734375 C 21.144531 -4.523438 17 -8.226562 14.28125 -12.84375 C 11.5625 -17.46875 10.203125 -22.570312 10.203125 -28.15625 C 10.203125 -37.539062 13.875 -45.15625 21.21875 -51 C 28.5625 -56.84375 39.578125 -59.765625 54.265625 -59.765625 L 79.96875 -59.765625 L 79.96875 -62.21875 C 79.96875 -69.15625 77.992188 -74.253906 74.046875 -77.515625 C 70.109375 -80.785156 65.210938 -82.421875 59.359375 -82.421875 C 54.054688 -82.421875 49.429688 -81.160156 45.484375 -78.640625 C 41.546875 -76.128906 39.097656 -72.425781 38.140625 -67.53125 L 12.640625 -67.53125 C 13.328125 -74.875 15.8125 -81.265625 20.09375 -86.703125 C 24.375 -92.140625 29.914062 -96.316406 36.71875 -99.234375 C 43.519531 -102.160156 51.132812 -103.625 59.5625 -103.625 C 73.976562 -103.625 85.332031 -100.019531 93.625 -92.8125 C 101.925781 -85.613281 106.078125 -75.414062 106.078125 -62.21875 L 106.078125 0 L 83.84375 0 L 81.390625 -16.3125 C 78.398438 -10.875 74.21875 -6.382812 68.84375 -2.84375 C 63.476562 0.6875 56.578125 2.453125 48.140625 2.453125 Z M 54.0625 -17.953125 C 61.539062 -17.953125 67.351562 -20.398438 71.5 -25.296875 C 75.644531 -30.191406 78.265625 -36.242188 79.359375 -43.453125 L 57.125 -43.453125 C 50.1875 -43.453125 45.21875 -42.191406 42.21875 -39.671875 C 39.226562 -37.160156 37.734375 -34.066406 37.734375 -30.390625 C 37.734375 -26.453125 39.226562 -23.394531 42.21875 -21.21875 C 45.21875 -19.039062 49.164062 -17.953125 54.0625 -17.953125 Z"/></g>
      <g transform="translate(803.385942, 822.044909)"><path d="M 13.265625 0 L 13.265625 -101.1875 L 36.3125 -101.1875 L 38.359375 -84.046875 C 41.484375 -90.035156 46.003906 -94.796875 51.921875 -98.328125 C 57.835938 -101.859375 64.804688 -103.625 72.828125 -103.625 C 85.335938 -103.625 95.0625 -99.679688 102 -91.796875 C 108.9375 -83.910156 112.40625 -72.351562 112.40625 -57.125 L 112.40625 0 L 86.296875 0 L 86.296875 -54.671875 C 86.296875 -63.378906 84.523438 -70.039062 80.984375 -74.65625 C 77.453125 -79.28125 71.945312 -81.59375 64.46875 -81.59375 C 57.125 -81.59375 51.101562 -79.007812 46.40625 -73.84375 C 41.71875 -68.675781 39.375 -61.46875 39.375 -52.21875 L 39.375 0 Z"/></g>
      <g transform="translate(927.417742, 822.044909)"><path d="M 59.765625 2.453125 C 50.242188 2.453125 41.742188 0.140625 34.265625 -4.484375 C 26.785156 -9.109375 20.867188 -15.429688 16.515625 -23.453125 C 12.171875 -31.484375 10 -40.597656 10 -50.796875 C 10 -60.992188 12.171875 -70.070312 16.515625 -78.03125 C 20.867188 -85.988281 26.785156 -92.242188 34.265625 -96.796875 C 41.742188 -101.347656 50.242188 -103.625 59.765625 -103.625 C 67.390625 -103.625 74.054688 -102.195312 79.765625 -99.34375 C 85.472656 -96.488281 90.097656 -92.476562 93.640625 -87.3125 L 93.640625 -146.875 L 119.75 -146.875 L 119.75 0 L 96.484375 0 L 93.640625 -14.484375 C 90.367188 -9.992188 86.046875 -6.046875 80.671875 -2.640625 C 75.304688 0.753906 68.335938 2.453125 59.765625 2.453125 Z M 65.28125 -20.40625 C 73.707031 -20.40625 80.609375 -23.222656 85.984375 -28.859375 C 91.359375 -34.503906 94.046875 -41.75 94.046875 -50.59375 C 94.046875 -59.4375 91.359375 -66.675781 85.984375 -72.3125 C 80.609375 -77.957031 73.707031 -80.78125 65.28125 -80.78125 C 56.988281 -80.78125 50.117188 -77.992188 44.671875 -72.421875 C 39.234375 -66.847656 36.515625 -59.640625 36.515625 -50.796875 C 36.515625 -41.953125 39.234375 -34.675781 44.671875 -28.96875 C 50.117188 -23.257812 56.988281 -20.40625 65.28125 -20.40625 Z"/></g>
      <g transform="translate(1060.425521, 822.044909)"><path d="M 62.421875 2.453125 C 52.222656 2.453125 43.175781 0.273438 35.28125 -4.078125 C 27.394531 -8.429688 21.207031 -14.550781 16.71875 -22.4375 C 12.238281 -30.320312 10 -39.4375 10 -49.78125 C 10 -60.25 12.207031 -69.5625 16.625 -77.71875 C 21.039062 -85.882812 27.160156 -92.242188 34.984375 -96.796875 C 42.804688 -101.347656 52.019531 -103.625 62.625 -103.625 C 72.550781 -103.625 81.320312 -101.445312 88.9375 -97.09375 C 96.5625 -92.75 102.515625 -86.800781 106.796875 -79.25 C 111.078125 -71.707031 113.21875 -63.3125 113.21875 -54.0625 C 113.21875 -52.5625 113.179688 -50.992188 113.109375 -49.359375 C 113.046875 -47.734375 112.945312 -46.035156 112.8125 -44.265625 L 35.90625 -44.265625 C 36.445312 -36.378906 39.195312 -30.191406 44.15625 -25.703125 C 49.125 -21.210938 55.144531 -18.96875 62.21875 -18.96875 C 67.519531 -18.96875 71.972656 -20.15625 75.578125 -22.53125 C 79.179688 -24.914062 81.867188 -28.015625 83.640625 -31.828125 L 110.15625 -31.828125 C 108.25 -25.429688 105.085938 -19.613281 100.671875 -14.375 C 96.253906 -9.144531 90.8125 -5.03125 84.34375 -2.03125 C 77.882812 0.957031 70.578125 2.453125 62.421875 2.453125 Z M 62.625 -82.421875 C 56.238281 -82.421875 50.597656 -80.617188 45.703125 -77.015625 C 40.804688 -73.410156 37.675781 -67.9375 36.3125 -60.59375 L 86.703125 -60.59375 C 86.296875 -67.25 83.847656 -72.550781 79.359375 -76.5 C 74.867188 -80.445312 69.289062 -82.421875 62.625 -82.421875 Z"/></g>
      <g transform="translate(1182.417355, 822.044909)"><path d="M 59.765625 2.453125 C 50.242188 2.453125 41.742188 0.140625 34.265625 -4.484375 C 26.785156 -9.109375 20.867188 -15.429688 16.515625 -23.453125 C 12.171875 -31.484375 10 -40.597656 10 -50.796875 C 10 -60.992188 12.171875 -70.070312 16.515625 -78.03125 C 20.867188 -85.988281 26.785156 -92.242188 34.265625 -96.796875 C 41.742188 -101.347656 50.242188 -103.625 59.765625 -103.625 C 67.390625 -103.625 74.054688 -102.195312 79.765625 -99.34375 C 85.472656 -96.488281 90.097656 -92.476562 93.640625 -87.3125 L 93.640625 -146.875 L 119.75 -146.875 L 119.75 0 L 96.484375 0 L 93.640625 -14.484375 C 90.367188 -9.992188 86.046875 -6.046875 80.671875 -2.640625 C 75.304688 0.753906 68.335938 2.453125 59.765625 2.453125 Z M 65.28125 -20.40625 C 73.707031 -20.40625 80.609375 -23.222656 85.984375 -28.859375 C 91.359375 -34.503906 94.046875 -41.75 94.046875 -50.59375 C 94.046875 -59.4375 91.359375 -66.675781 85.984375 -72.3125 C 80.609375 -77.957031 73.707031 -80.78125 65.28125 -80.78125 C 56.988281 -80.78125 50.117188 -77.992188 44.671875 -72.421875 C 39.234375 -66.847656 36.515625 -59.640625 36.515625 -50.796875 C 36.515625 -41.953125 39.234375 -34.675781 44.671875 -28.96875 C 50.117188 -23.257812 56.988281 -20.40625 65.28125 -20.40625 Z"/></g>
    </g>
  </svg>
);

// Warm White logo: light bg, navy "Job" + teal "anded" — used in light mode header
const LogoWarmWhite = ({ height = 40 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="100 530 1250 400" style={{ height, width: "auto", display: "block" }}>
    <defs>
      <clipPath id="ww-clip1"><path d="M 829 564 L 845.023438 564 L 845.023438 583 L 829 583 Z" clipRule="nonzero"/></clipPath>
      <clipPath id="ww-clip2"><path d="M 501.523438 554.167969 L 836 554.167969 L 836 896.917969 L 501.523438 896.917969 Z" clipRule="nonzero"/></clipPath>
    </defs>
    {/* Bird / checkmark mark */}
    <g fill="#2a7f6f">
      <path d="M 818.898438 558.914062 L 814.265625 565.089844 C 815.265625 564.960938 820.046875 559.289062 818.898438 558.914062 Z"/>
      <g clipPath="url(#ww-clip1)"><path d="M 844.628906 564.058594 L 829.246094 580.234375 L 829.707031 582.070312 C 831.816406 578.378906 834.851562 576.277344 837.683594 573.066406 C 837.867188 572.859375 837.5 571.726562 837.683594 571.523438 C 838.109375 571.042969 847.777344 563.636719 844.628906 564.058594 Z"/></g>
      <path d="M 824.558594 585.160156 L 829.246094 580.234375 L 829.191406 580.015625 Z"/>
      <path d="M 804.226562 571.265625 C 804.996094 570.34375 805.453125 568.683594 806.800781 567.921875 C 806.828125 566.433594 806.097656 567.839844 805.773438 567.664062 L 798.570312 575.125 C 798.265625 577.394531 803.9375 571.617188 804.226562 571.265625 Z"/>
      <path d="M 745.042969 632.253906 C 748.429688 631.589844 765.226562 609.757812 766.914062 612.4375 C 767.835938 611.65625 771.253906 605.238281 772.835938 607.550781 C 771.898438 608.078125 772.050781 608.679688 771.808594 609.351562 C 773.589844 606.976562 775.941406 608.109375 772.324219 604.976562 C 772.578125 601.921875 796.386719 584.988281 796.253906 578.46875 L 742.984375 632.769531 C 744.074219 633.605469 744.410156 632.65625 745.042969 632.253906 Z"/>
      <path d="M 819.925781 583.617188 L 820.441406 584.132812 C 822.050781 584.039062 820.808594 583.4375 819.925781 583.617188 Z"/>
      <path d="M 827.644531 584.132812 L 822.757812 588.507812 C 823.121094 589.644531 828.339844 585.554688 827.644531 584.132812 Z"/>
      <path d="M 826.359375 591.851562 C 826.140625 594.648438 828.789062 591.175781 829.449219 590.824219 C 827.59375 589.34375 828.753906 590.429688 826.359375 591.851562 Z"/>
      <path d="M 812.722656 592.367188 C 812.34375 592.480469 811.539062 593.453125 812.207031 593.910156 C 812.988281 593.386719 814.039062 591.976562 812.722656 592.367188 Z"/>
      <g clipPath="url(#ww-clip2)"><path d="M 795.738281 634.570312 C 794.453125 635.390625 788.4375 638.710938 787.242188 637.402344 C 788.53125 635.628906 793.085938 634.304688 793.933594 632.769531 C 794.425781 631.875 794.25 628.164062 795.21875 627.882812 C 797.875 632.503906 806.875 615.121094 811.429688 614.242188 C 808.085938 613.851562 810.734375 610.4375 809.113281 610.382812 C 808.691406 610.367188 804.140625 615.679688 803.191406 616.300781 L 805.769531 616.816406 L 801.914062 621.1875 L 801.398438 619.128906 L 797.28125 623.246094 C 797.359375 622.445312 796.460938 620.066406 796.507812 619.902344 C 796.757812 619.035156 809.714844 609.785156 808.859375 607.039062 C 808.824219 606.929688 806.28125 607.785156 807.316406 606.007812 C 807.480469 605.726562 811.984375 606.199219 812.460938 602.921875 L 809.117188 605.238281 L 809.632812 603.179688 C 811.445312 602.914062 816.703125 597.195312 817.351562 597.003906 C 818.273438 596.730469 818.878906 599.15625 820.183594 597.261719 C 817.15625 595.699219 820.152344 594.761719 821.210938 592.886719 C 821.101562 592.925781 818.746094 593.292969 818.894531 592.371094 L 788.53125 620.675781 L 804.738281 603.949219 L 804.996094 602.148438 C 803.144531 601.292969 782.488281 625.308594 780.808594 625.308594 C 780.714844 625.308594 779.523438 624.113281 779.523438 624.023438 C 779.523438 622.695312 797.328125 608.953125 799.082031 606.011719 L 798.824219 603.179688 L 765.113281 634.574219 C 777.933594 619.292969 794.429688 607.464844 806.800781 591.859375 L 808.859375 592.886719 C 805.855469 595.9375 802.753906 599.109375 800.113281 602.40625 C 801.257812 604.378906 813.484375 590.335938 814.011719 588.769531 C 816.601562 589.859375 817.0625 586.171875 817.613281 584.648438 C 817.867188 583.941406 819.417969 583.09375 818.128906 582.589844 C 819.144531 581.53125 819.398438 581.269531 820.703125 580.53125 C 821.386719 580.144531 822.457031 580.09375 823.019531 578.988281 L 823.019531 581.046875 C 826.992188 577.59375 823.90625 579.167969 823.792969 577.703125 C 826.84375 574.640625 830.699219 571.589844 831.511719 567.152344 C 831.511719 567.152344 835.632812 562.265625 835.632812 562.265625 L 832.542969 564.582031 L 832.027344 564.582031 C 832.679688 563.15625 825.8125 567.800781 825.339844 568.183594 C 824.414062 569.066406 819.671875 573.878906 818.652344 574.875 C 816.054688 574.714844 818.941406 573.058594 818.394531 571.789062 C 816.789062 573.789062 814.296875 575.429688 813.5 577.964844 C 813.285156 578.214844 812.929688 578.28125 812.472656 578.996094 L 810.414062 579.253906 C 811.808594 576.566406 819.109375 565.875 821.996094 566.644531 C 825.023438 563.761719 827.40625 558.003906 830.230469 554.292969 L 808.871094 576.425781 C 780.851562 597.328125 762.695312 624.964844 741.453125 650.535156 C 740.394531 651.445312 728.835938 666.921875 730.644531 660.316406 C 717.601562 676.851562 703.445312 692.625 690.5 709.464844 L 688.183594 710.238281 L 687.925781 707.921875 C 684.441406 713.285156 679.902344 721.171875 675.574219 725.679688 C 674.234375 727.078125 671.769531 730.261719 670.941406 727.480469 C 670.839844 726.476562 675.851562 722.421875 676.601562 721.304688 C 676.90625 720.855469 675.570312 720.46875 676.601562 718.988281 C 685.242188 706.574219 698.023438 697.503906 706.453125 686.304688 C 709.101562 679.46875 713.820312 672.886719 719.835938 668.292969 L 716.746094 668.808594 L 729.355469 655.171875 C 726.050781 657.457031 721.386719 659.515625 718.804688 662.632812 C 704.429688 679.976562 689.253906 701.226562 676.089844 718.988281 C 670.402344 726.664062 666.042969 730.785156 673.515625 718.730469 C 672.820312 718.355469 668.988281 723.898438 668.882812 721.304688 C 669.804688 718.359375 683.402344 705.363281 682.78125 703.804688 C 681.636719 700.941406 675.136719 708.304688 674.03125 709.464844 C 647.621094 738.359375 628.632812 773.527344 603.261719 803.390625 C 587.253906 782.972656 570.203125 763.523438 551.28125 745.746094 C 549.921875 749.210938 550.191406 745.355469 548.449219 746.519531 C 568.019531 776.164062 536.570312 733.535156 534.039062 737.257812 L 538.925781 746.777344 C 537.152344 747.125 529.691406 737.21875 528.890625 739.832031 C 528.8125 740.089844 531.300781 744.394531 527.605469 743.691406 C 526.71875 743.523438 523.804688 738.386719 522.96875 739.058594 L 523.226562 743.433594 L 520.910156 742.148438 C 517.019531 745.511719 520.988281 748.792969 520.652344 750.128906 C 519.8125 753.503906 515.75 748.664062 515.25 748.839844 C 514.6875 749.039062 514.074219 752.046875 513.964844 752.1875 C 513.535156 752.730469 511.878906 752.03125 511.390625 753.214844 C 511.167969 753.757812 516.117188 764.554688 510.617188 757.589844 C 506.972656 762.300781 508.703125 767.8125 501.609375 761.964844 C 501.035156 763.757812 503.785156 771.257812 503.667969 771.746094 C 503.5 772.457031 501.636719 773.164062 501.609375 773.289062 C 502.070312 780.386719 510.507812 785.71875 515.507812 790.789062 C 516.667969 792.019531 518.269531 796.601562 518.59375 796.964844 C 532.074219 804.816406 543.78125 820.074219 554.105469 831.964844 C 563.171875 842.734375 594.84375 889.683594 602.230469 893.980469 C 611.109375 899.148438 618.128906 895.859375 623.847656 888.320312 L 622.046875 891.664062 C 622.992188 892.308594 624.070312 890.695312 624.621094 890.121094 C 641.53125 872.0625 651.09375 847.527344 666.308594 828.359375 L 664.765625 827.332031 C 676.144531 818.027344 669.667969 814.5 675.570312 812.410156 C 675.617188 812.273438 674.21875 811.476562 674.542969 810.863281 C 675.726562 808.632812 676.929688 806.386719 678.144531 804.175781 C 678.625 803.308594 680.089844 802.832031 680.203125 802.632812 C 677.570312 800.191406 692.464844 785.371094 694.097656 781.53125 C 696.332031 776.203125 701.574219 769.058594 704.910156 763.773438 C 706.949219 760.78125 710.585938 757.683594 711.34375 753.992188 L 704.398438 763.773438 C 700.933594 766.820312 703.496094 763.542969 702.082031 763.773438 C 700.542969 768.101562 687.929688 781.769531 684.839844 785.390625 C 695.03125 769.570312 705.351562 753.691406 716.230469 738.300781 C 720.785156 732.195312 728.242188 725.945312 732.1875 720.289062 C 732.234375 720.222656 731.207031 719.46875 731.671875 718.742188 C 733.066406 716.5625 735.378906 714.679688 736.816406 711.539062 C 736.992188 711.15625 734.183594 709.707031 739.390625 708.964844 L 736.816406 705.878906 C 740.890625 699.9375 746.503906 695.242188 751.226562 689.921875 C 751.28125 689.859375 750.203125 688.949219 750.710938 688.378906 C 751.484375 687.507812 754.066406 685.003906 755.085938 684.003906 C 756.953125 683.5 760.664062 682.128906 759.464844 679.628906 C 761.953125 676.980469 764.964844 673.585938 767.441406 671.136719 C 767.542969 671.039062 769.398438 671.460938 769.757812 670.363281 C 770.238281 668.898438 767.855469 668.679688 767.699219 667.789062 C 767.488281 666.570312 770.933594 663.613281 771.816406 662.644531 C 772 662.445312 771.636719 661.296875 771.816406 661.101562 C 772.957031 659.871094 773.679688 661.332031 774.132812 660.84375 C 777.941406 656.75 780.675781 650.90625 786.484375 651.0625 C 788.105469 651.109375 788.53125 653.449219 790.601562 651.578125 C 793.484375 648.972656 789.53125 649.675781 789.316406 648.234375 C 789.195312 647.441406 797.21875 635.851562 798.066406 634.851562 C 801.539062 630.746094 800.007812 634.105469 801.410156 634.078125 C 802.886719 632.238281 812.210938 622.757812 812.472656 621.472656 C 812.71875 620.273438 812.339844 619.125 811.699219 618.125 C 806.800781 623.386719 802.101562 630.5 795.738281 634.570312 Z"/></g>
    </g>
    {/* "Job" — navy */}
    <g fill="#1c2b3a">
      <g transform="translate(172.441425, 892.735679)"><path d="M 51.21875 2.453125 C 37.34375 2.453125 26.222656 -1.457031 17.859375 -9.28125 C 9.492188 -17.101562 5.3125 -28.222656 5.3125 -42.640625 L 31.421875 -42.640625 C 31.554688 -35.972656 33.117188 -30.664062 36.109375 -26.71875 C 39.109375 -22.78125 43.941406 -20.8125 50.609375 -20.8125 C 56.859375 -20.8125 61.410156 -22.679688 64.265625 -26.421875 C 67.128906 -30.160156 68.5625 -35.09375 68.5625 -41.21875 L 68.5625 -142.828125 L 94.671875 -142.828125 L 94.671875 -41.21875 C 94.671875 -27.207031 90.757812 -16.425781 82.9375 -8.875 C 75.125 -1.320312 64.550781 2.453125 51.21875 2.453125 Z"/></g>
      <g transform="translate(280.997163, 892.735679)"><path d="M 61.828125 2.453125 C 52.035156 2.453125 43.226562 0.207031 35.40625 -4.28125 C 27.582031 -8.769531 21.390625 -14.992188 16.828125 -22.953125 C 12.273438 -30.910156 10 -40.128906 10 -50.609375 C 10 -61.078125 12.3125 -70.289062 16.9375 -78.25 C 21.5625 -86.207031 27.785156 -92.429688 35.609375 -96.921875 C 43.429688 -101.410156 52.238281 -103.65625 62.03125 -103.65625 C 71.6875 -103.65625 80.425781 -101.410156 88.25 -96.921875 C 96.070312 -92.429688 102.257812 -86.207031 106.8125 -78.25 C 111.375 -70.289062 113.65625 -61.078125 113.65625 -50.609375 C 113.65625 -40.128906 111.375 -30.910156 106.8125 -22.953125 C 102.257812 -14.992188 96.035156 -8.769531 88.140625 -4.28125 C 80.253906 0.207031 71.484375 2.453125 61.828125 2.453125 Z M 61.828125 -20.203125 C 68.628906 -20.203125 74.546875 -22.75 79.578125 -27.84375 C 84.609375 -32.945312 87.125 -40.535156 87.125 -50.609375 C 87.125 -60.671875 84.609375 -68.25 79.578125 -73.34375 C 74.546875 -78.445312 68.695312 -81 62.03125 -81 C 55.09375 -81 49.140625 -78.445312 44.171875 -73.34375 C 39.210938 -68.25 36.734375 -60.671875 36.734375 -50.609375 C 36.734375 -40.535156 39.210938 -32.945312 44.171875 -27.84375 C 49.140625 -22.75 55.023438 -20.203125 61.828125 -20.203125 Z"/></g>
      <g transform="translate(404.652762, 892.735679)"><path d="M 73.25 2.453125 C 65.632812 2.453125 58.96875 1.023438 53.25 -1.828125 C 47.539062 -4.691406 42.914062 -8.707031 39.375 -13.875 L 36.53125 0 L 13.265625 0 L 13.265625 -146.90625 L 39.375 -146.90625 L 39.375 -86.71875 C 42.644531 -91.207031 46.960938 -95.148438 52.328125 -98.546875 C 57.703125 -101.953125 64.675781 -103.65625 73.25 -103.65625 C 82.769531 -103.65625 91.269531 -101.34375 98.75 -96.71875 C 106.238281 -92.09375 112.160156 -85.765625 116.515625 -77.734375 C 120.867188 -69.710938 123.046875 -60.601562 123.046875 -50.40625 C 123.046875 -40.195312 120.867188 -31.113281 116.515625 -23.15625 C 112.160156 -15.195312 106.238281 -8.9375 98.75 -4.375 C 91.269531 0.175781 82.769531 2.453125 73.25 2.453125 Z M 67.75 -20.40625 C 76.039062 -20.40625 82.90625 -23.191406 88.34375 -28.765625 C 93.789062 -34.347656 96.515625 -41.5625 96.515625 -50.40625 C 96.515625 -59.238281 93.789062 -66.507812 88.34375 -72.21875 C 82.90625 -77.9375 76.039062 -80.796875 67.75 -80.796875 C 59.3125 -80.796875 52.40625 -77.972656 47.03125 -72.328125 C 41.65625 -66.679688 38.96875 -59.441406 38.96875 -50.609375 C 38.96875 -41.765625 41.65625 -34.519531 47.03125 -28.875 C 52.40625 -23.226562 59.3125 -20.40625 67.75 -20.40625 Z"/></g>
    </g>
    <g fill="#2a7f6f">
      <g transform="translate(667.534075, 892.730519)"><path d="M 48.140625 2.453125 C 39.441406 2.453125 32.300781 1.054688 26.71875 -1.734375 C 21.144531 -4.523438 17 -8.226562 14.28125 -12.84375 C 11.5625 -17.46875 10.203125 -22.570312 10.203125 -28.15625 C 10.203125 -37.539062 13.875 -45.15625 21.21875 -51 C 28.5625 -56.84375 39.578125 -59.765625 54.265625 -59.765625 L 79.96875 -59.765625 L 79.96875 -62.21875 C 79.96875 -69.15625 77.992188 -74.253906 74.046875 -77.515625 C 70.109375 -80.785156 65.210938 -82.421875 59.359375 -82.421875 C 54.054688 -82.421875 49.429688 -81.160156 45.484375 -78.640625 C 41.546875 -76.128906 39.097656 -72.425781 38.140625 -67.53125 L 12.640625 -67.53125 C 13.328125 -74.875 15.8125 -81.265625 20.09375 -86.703125 C 24.375 -92.140625 29.914062 -96.316406 36.71875 -99.234375 C 43.519531 -102.160156 51.132812 -103.625 59.5625 -103.625 C 73.976562 -103.625 85.332031 -100.019531 93.625 -92.8125 C 101.925781 -85.613281 106.078125 -75.414062 106.078125 -62.21875 L 106.078125 0 L 83.84375 0 L 81.390625 -16.3125 C 78.398438 -10.875 74.21875 -6.382812 68.84375 -2.84375 C 63.476562 0.6875 56.578125 2.453125 48.140625 2.453125 Z M 54.0625 -17.953125 C 61.539062 -17.953125 67.351562 -20.398438 71.5 -25.296875 C 75.644531 -30.191406 78.265625 -36.242188 79.359375 -43.453125 L 57.125 -43.453125 C 50.1875 -43.453125 45.21875 -42.191406 42.21875 -39.671875 C 39.226562 -37.160156 37.734375 -34.066406 37.734375 -30.390625 C 37.734375 -26.453125 39.226562 -23.394531 42.21875 -21.21875 C 45.21875 -19.039062 49.164062 -17.953125 54.0625 -17.953125 Z"/></g>
      <g transform="translate(785.241848, 892.730519)"><path d="M 13.265625 0 L 13.265625 -101.1875 L 36.3125 -101.1875 L 38.359375 -84.046875 C 41.484375 -90.035156 46.003906 -94.796875 51.921875 -98.328125 C 57.835938 -101.859375 64.804688 -103.625 72.828125 -103.625 C 85.335938 -103.625 95.0625 -99.679688 102 -91.796875 C 108.9375 -83.910156 112.40625 -72.351562 112.40625 -57.125 L 112.40625 0 L 86.296875 0 L 86.296875 -54.671875 C 86.296875 -63.378906 84.523438 -70.039062 80.984375 -74.65625 C 77.453125 -79.28125 71.945312 -81.59375 64.46875 -81.59375 C 57.125 -81.59375 51.101562 -79.007812 46.40625 -73.84375 C 41.71875 -68.675781 39.375 -61.46875 39.375 -52.21875 L 39.375 0 Z"/></g>
      <g transform="translate(909.273648, 892.730519)"><path d="M 59.765625 2.453125 C 50.242188 2.453125 41.742188 0.140625 34.265625 -4.484375 C 26.785156 -9.109375 20.867188 -15.429688 16.515625 -23.453125 C 12.171875 -31.484375 10 -40.597656 10 -50.796875 C 10 -60.992188 12.171875 -70.070312 16.515625 -78.03125 C 20.867188 -85.988281 26.785156 -92.242188 34.265625 -96.796875 C 41.742188 -101.347656 50.242188 -103.625 59.765625 -103.625 C 67.390625 -103.625 74.054688 -102.195312 79.765625 -99.34375 C 85.472656 -96.488281 90.097656 -92.476562 93.640625 -87.3125 L 93.640625 -146.875 L 119.75 -146.875 L 119.75 0 L 96.484375 0 L 93.640625 -14.484375 C 90.367188 -9.992188 86.046875 -6.046875 80.671875 -2.640625 C 75.304688 0.753906 68.335938 2.453125 59.765625 2.453125 Z M 65.28125 -20.40625 C 73.707031 -20.40625 80.609375 -23.222656 85.984375 -28.859375 C 91.359375 -34.503906 94.046875 -41.75 94.046875 -50.59375 C 94.046875 -59.4375 91.359375 -66.675781 85.984375 -72.3125 C 80.609375 -77.957031 73.707031 -80.78125 65.28125 -80.78125 C 56.988281 -80.78125 50.117188 -77.992188 44.671875 -72.421875 C 39.234375 -66.847656 36.515625 -59.640625 36.515625 -50.796875 C 36.515625 -41.953125 39.234375 -34.675781 44.671875 -28.96875 C 50.117188 -23.257812 56.988281 -20.40625 65.28125 -20.40625 Z"/></g>
      <g transform="translate(1042.281427, 892.730519)"><path d="M 62.421875 2.453125 C 52.222656 2.453125 43.175781 0.273438 35.28125 -4.078125 C 27.394531 -8.429688 21.207031 -14.550781 16.71875 -22.4375 C 12.238281 -30.320312 10 -39.4375 10 -49.78125 C 10 -60.25 12.207031 -69.5625 16.625 -77.71875 C 21.039062 -85.882812 27.160156 -92.242188 34.984375 -96.796875 C 42.804688 -101.347656 52.019531 -103.625 62.625 -103.625 C 72.550781 -103.625 81.320312 -101.445312 88.9375 -97.09375 C 96.5625 -92.75 102.515625 -86.800781 106.796875 -79.25 C 111.078125 -71.707031 113.21875 -63.3125 113.21875 -54.0625 C 113.21875 -52.5625 113.179688 -50.992188 113.109375 -49.359375 C 113.046875 -47.734375 112.945312 -46.035156 112.8125 -44.265625 L 35.90625 -44.265625 C 36.445312 -36.378906 39.195312 -30.191406 44.15625 -25.703125 C 49.125 -21.210938 55.144531 -18.96875 62.21875 -18.96875 C 67.519531 -18.96875 71.972656 -20.15625 75.578125 -22.53125 C 79.179688 -24.914062 81.867188 -28.015625 83.640625 -31.828125 L 110.15625 -31.828125 C 108.25 -25.429688 105.085938 -19.613281 100.671875 -14.375 C 96.253906 -9.144531 90.8125 -5.03125 84.34375 -2.03125 C 77.882812 0.957031 70.578125 2.453125 62.421875 2.453125 Z M 62.625 -82.421875 C 56.238281 -82.421875 50.597656 -80.617188 45.703125 -77.015625 C 40.804688 -73.410156 37.675781 -67.9375 36.3125 -60.59375 L 86.703125 -60.59375 C 86.296875 -67.25 83.847656 -72.550781 79.359375 -76.5 C 74.867188 -80.445312 69.289062 -82.421875 62.625 -82.421875 Z"/></g>
      <g transform="translate(1164.27326, 892.730519)"><path d="M 59.765625 2.453125 C 50.242188 2.453125 41.742188 0.140625 34.265625 -4.484375 C 26.785156 -9.109375 20.867188 -15.429688 16.515625 -23.453125 C 12.171875 -31.484375 10 -40.597656 10 -50.796875 C 10 -60.992188 12.171875 -70.070312 16.515625 -78.03125 C 20.867188 -85.988281 26.785156 -92.242188 34.265625 -96.796875 C 41.742188 -101.347656 50.242188 -103.625 59.765625 -103.625 C 67.390625 -103.625 74.054688 -102.195312 79.765625 -99.34375 C 85.472656 -96.488281 90.097656 -92.476562 93.640625 -87.3125 L 93.640625 -146.875 L 119.75 -146.875 L 119.75 0 L 96.484375 0 L 93.640625 -14.484375 C 90.367188 -9.992188 86.046875 -6.046875 80.671875 -2.640625 C 75.304688 0.753906 68.335938 2.453125 59.765625 2.453125 Z M 65.28125 -20.40625 C 73.707031 -20.40625 80.609375 -23.222656 85.984375 -28.859375 C 91.359375 -34.503906 94.046875 -41.75 94.046875 -50.59375 C 94.046875 -59.4375 91.359375 -66.675781 85.984375 -72.3125 C 80.609375 -77.957031 73.707031 -80.78125 65.28125 -80.78125 C 56.988281 -80.78125 50.117188 -77.992188 44.671875 -72.421875 C 39.234375 -66.847656 36.515625 -59.640625 36.515625 -50.796875 C 36.515625 -41.953125 39.234375 -34.675781 44.671875 -28.96875 C 50.117188 -23.257812 56.988281 -20.40625 65.28125 -20.40625 Z"/></g>
    </g>
  </svg>
);

// ── ACCESS GATE ─────────────────────────────────────────────────────────────

const isLocalhost = typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

function AccessGate({ onGranted }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/validate-access-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json();
      if (data.valid) {
        try { localStorage.setItem("jl_access_granted", "true"); } catch {}
        onGranted();
      } else {
        setError("That code isn't valid. Check your email or purchase at the link below.");
      }
    } catch {
      setError("Something went wrong. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === "Enter") submit(); };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#1C2B3A",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'DM Sans', sans-serif",
      padding: "24px",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-5px)} 40%,80%{transform:translateX(5px)} }
        .gate-card { animation: fadeUp 0.4s ease forwards; }
        .gate-input:focus { outline: none; border-color: #2A7F6F !important; }
        .gate-btn-primary:hover { opacity: 0.88; }
        .gate-btn-secondary:hover { background: rgba(42,127,111,0.08) !important; }
        .gate-error { animation: shake 0.35s ease; }
      `}</style>

      <div className="gate-card" style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>

        {/* Logo — hero size */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 36 }}>
          <LogoInk height={56} />
        </div>

        {/* Headline */}
        <h1 style={{
          fontFamily: "'Fraunces', serif",
          fontSize: "1.55rem",
          color: "#F8F5F3",
          margin: "0 0 10px",
          letterSpacing: -0.5,
          lineHeight: 1.25,
        }}>
          Your Application Command Center.
        </h1>
        <p style={{ color: "rgba(248,245,243,0.55)", fontSize: "0.85rem", margin: "0 0 8px", fontWeight: 300 }}>
          Fit First. Apply Fast.
        </p>

        {/* Divider */}
        <div style={{ width: 40, height: 2, background: "#2A7F6F", margin: "24px auto 32px", borderRadius: 2 }} />

        {/* Instructions */}
        <p style={{ color: "rgba(248,245,243,0.65)", fontSize: "0.83rem", lineHeight: 1.75, margin: "0 0 28px" }}>
          JobLanded is currently available to founding members and invited users.
        </p>

        {/* Input */}
        <input
          className="gate-input"
          type="text"
          placeholder="Enter your access code"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          onKeyDown={handleKey}
          style={{
            width: "100%",
            padding: "14px 16px",
            fontSize: "1rem",
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: "0.04em",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 8,
            color: "#F8F5F3",
            marginBottom: 12,
            boxSizing: "border-box",
            textAlign: "center",
            transition: "border-color 0.2s",
          }}
          autoFocus
          disabled={loading}
        />

        {/* Error */}
        {error && (
          <p className="gate-error" style={{
            color: "#c97070",
            fontSize: "0.78rem",
            marginBottom: 12,
            lineHeight: 1.5,
          }}>
            {error}
          </p>
        )}

        {/* Primary button — Unlock */}
        <button
          className="gate-btn-primary"
          onClick={submit}
          disabled={loading || !code.trim()}
          style={{
            width: "100%",
            padding: "14px",
            background: loading || !code.trim() ? "rgba(42,127,111,0.4)" : "#2A7F6F",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: "0.9rem",
            fontWeight: 600,
            fontFamily: "'DM Sans', sans-serif",
            cursor: loading || !code.trim() ? "not-allowed" : "pointer",
            transition: "opacity 0.2s, background 0.2s",
            letterSpacing: "0.02em",
            marginBottom: 12,
          }}
        >
          {loading ? "Checking…" : "Unlock JobLanded →"}
        </button>

        {/* Divider between buttons */}
        <p style={{ color: "rgba(248,245,243,0.25)", fontSize: "0.75rem", margin: "0 0 12px" }}>or</p>

        {/* Secondary button — Get Access */}
        <a
          href="https://joblanded.app/pricing"
          target="_blank"
          rel="noopener noreferrer"
          className="gate-btn-secondary"
          style={{
            display: "block",
            width: "100%",
            padding: "13px",
            background: "transparent",
            color: "#2A7F6F",
            border: "1.5px solid #2A7F6F",
            borderRadius: 8,
            fontSize: "0.9rem",
            fontWeight: 600,
            fontFamily: "'DM Sans', sans-serif",
            cursor: "pointer",
            transition: "background 0.2s",
            letterSpacing: "0.02em",
            textDecoration: "none",
            boxSizing: "border-box",
          }}
        >
          Get Access →
        </a>

        {/* Footer */}
        <p style={{ color: "rgba(248,245,243,0.25)", fontSize: "0.68rem", marginTop: 32 }}>
          JobLanded · Founding Member Beta
        </p>
      </div>
    </div>
  );
}

// ── MAIN APP ───────────────────────────────────────────────────────────────

export default function App() {
  // ── Access Gate state ──────────────────────────────────────────────────
  const [accessGranted, setAccessGranted] = useState(isLocalhost); // localhost bypasses gate
  const [accessChecked, setAccessChecked] = useState(isLocalhost);

  const [profile, setProfile] = useState(null);
  const [appReady, setAppReady] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [tab, setTab] = useState("generate");
  const [darkMode, setDarkMode] = useState(true);
  const C = makeColors(darkMode);

  const [company, setCompany] = useState("");
  const [jobtitle, setJobtitle] = useState("");
  const [jobdesc, setJobdesc] = useState("");
  const [jobLink, setJobLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showClarity, setShowClarity] = useState(false);
  const [error, setError] = useState("");
  const [applications, setApplications] = useState([]);
  const [modalApp, setModalApp] = useState(null);
  const [filter, setFilter] = useState("all");
  const [toast, setToast] = useState("");
  const [openQA, setOpenQA] = useState(null);
  const [lastTemplate, setLastTemplate] = useState("traditional");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const saveApps = (apps) => {
    setApplications(apps);
    try { localStorage.setItem("jl_apps", JSON.stringify(apps)); } catch {}
  };

  const saveProfile = (p) => {
    setProfile(p);
    setShowOnboarding(false);
    try { localStorage.setItem("jl_profile", JSON.stringify(p)); } catch {}
  };

  useEffect(() => {
    async function init() {
      // Check access grant from localStorage (set by AccessGate on success)
      if (!isLocalhost) {
        try {
          const saved = localStorage.getItem("jl_access_granted");
          if (saved === "true") {
            setAccessGranted(true);
          }
        } catch {}
        setAccessChecked(true);
      }

      try {
        const rawProfile  = localStorage.getItem("jl_profile");
        const rawApps     = localStorage.getItem("jl_apps");
        const rawDarkMode = localStorage.getItem("jl_darkmode");
        const savedProfile = rawProfile ? JSON.parse(rawProfile) : null;
        const savedApps    = rawApps    ? JSON.parse(rawApps)    : [];
        if (savedProfile) setProfile(savedProfile);
        if (savedApps.length) setApplications(savedApps);
        if (rawDarkMode !== null) setDarkMode(rawDarkMode !== "false");
        // Show welcome back screen if returning user with a name
        if (savedProfile?.name) setShowWelcome(true);
      } catch {}
      setAppReady(true);
    }
    init();
  }, []);

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    try { localStorage.setItem("jl_darkmode", String(next)); } catch {}
  };

  const fitColors = {
    high:   { border: "rgba(42,127,111,0.4)", bg: "rgba(42,127,111,0.08)", circle: "rgba(42,127,111,0.2)", circleBorder: "rgba(42,127,111,0.6)", text: "#3A9A87" },
    medium: { border: "rgba(243,156,18,0.3)", bg: "rgba(243,156,18,0.07)", circle: "rgba(243,156,18,0.18)", circleBorder: "rgba(243,156,18,0.5)", text: "#f39c12" },
    low:    { border: "rgba(180,80,80,0.25)", bg: "rgba(180,80,80,0.05)", circle: "rgba(180,80,80,0.15)", circleBorder: "rgba(180,80,80,0.35)", text: "#c97070" },
  };

  const statusConfig = {
    saved: { bg: "rgba(74,95,114,0.2)", color: C.bluGray, label: "Saved" },
    applied: { bg: "rgba(42,127,111,0.15)", color: C.tealBright, label: "Applied" },
    interview: { bg: "rgba(243,156,18,0.15)", color: "#f39c12", label: "Interview" },
    offer: { bg: "rgba(58,154,135,0.2)", color: C.tealLight, label: "Offer" },
    declined: { bg: "rgba(180,80,80,0.12)", color: "#c97070", label: "Declined" },
    followup: { bg: "rgba(42,127,111,0.12)", color: C.teal, label: "Follow-Up" },
  };

  const stats = {
    applied: applications.filter(a => ["applied","interview","offer"].includes(a.status)).length,
    interviews: applications.filter(a => ["interview","offer"].includes(a.status)).length,
    saved: applications.filter(a => a.status === "saved").length,
  };

  const inputStyle = { width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: "inherit", fontSize: "0.95rem", padding: "13px 15px", outline: "none", transition: "border-color 0.2s" };

  const generate = async () => {
    if (!jobdesc.trim()) return;

    const styleRecommendation = recommendResumeTemplate(jobtitle, jobdesc);
    setLastTemplate(styleRecommendation.template);

    setLoading(true); setResult(null); setError(""); setShowClarity(false);
    try {
      const res = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 6000, messages: [{ role: "user", content: buildPrompt(profile, company, jobtitle, jobdesc) }] })
      });
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();
      const text = data.content.map(i => i.text || "").join("");
      const extract = (key) => {
        const start = text.indexOf(`===${key}===`);
        if (start === -1) return "";
        const contentStart = start + key.length + 6;
        const nextSection = text.indexOf("===", contentStart);
        const raw = nextSection === -1 ? text.slice(contentStart) : text.slice(contentStart, nextSection);
        return raw.trim();
      };
      const score = parseInt(extract("FIT_SCORE")) || 0;
      const level = score >= 75 ? "high" : score >= 50 ? "medium" : "low";
      const realityRaw = extract("REALITY_FLAGS");
      const hasFlags = realityRaw && realityRaw !== "CLEAR" && realityRaw.includes("⚠");
      const keyNotes = extract("KEY_NOTES");
      setResult({
        company, jobtitle, jobdesc, score, level,
        realityFlags: hasFlags ? realityRaw : null,
        verdict: extract("FIT_VERDICT"),
        reasoning: extract("FIT_REASONING"),
        keywords: extract("KEYWORDS"),
        resumeFull: extract("RESUME_FULL"),
        cover: extract("COVER_LETTER"),
        followup: extract("FOLLOWUP_EMAIL"),
        keyNotes,
        hasWarnings: keyNotes && !keyNotes.includes("No special instructions"),
        recommendedTemplate: styleRecommendation.template,
        recommendedReason: styleRecommendation.reason,
        recommendedConfidence: styleRecommendation.confidence,
        resumeTemplateId: styleRecommendation.template,
        date: new Date().toISOString(), status: "applied",
      });
      setShowClarity(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const saveToTracker = (status = "applied") => {
    if (!result) return;
    saveApps([{ ...result, id: Date.now(), status, resumeTemplateId: result.resumeTemplateId || lastTemplate, jobLink: jobLink.trim() }, ...applications]);
    setShowClarity(false);
    setResult(null); setCompany(""); setJobtitle(""); setJobdesc(""); setJobLink("");
    showToast(status === "saved" ? "◈ Saved for later!" : "✓ Saved to tracker!");
  };

  const exportCSV = () => {
    const rows = [["Job Title","Company","Date","Fit Score","Status"], ...applications.map(a => [`"${(a.jobtitle||"").replace(/"/g,'""')}"`,`"${(a.company||"").replace(/"/g,'""')}"`,`"${new Date(a.date).toLocaleDateString()}"`,a.score,a.status])];
    const uri = "data:text/csv;charset=utf-8," + encodeURIComponent(rows.map(r => r.join(",")).join("\n"));
    const a = document.createElement("a"); a.href = uri; a.download = "joblanded_tracker.csv";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const filtered = filter === "all" ? applications
    : filter === "followup" ? applications.filter(a => a.status === "applied" && (Date.now() - new Date(a.date)) / 86400000 >= 5)
    : applications.filter(a => a.status === filter);

  const quickAnswers = profile ? buildQuickAnswers(profile) : [];

  // ── Access Gate ──────────────────────────────────────────────────────────
  // Show nothing until we've checked localStorage (avoids flash)
  if (!accessChecked) return null;
  // Show gate if not yet granted
  if (!accessGranted) return <AccessGate onGranted={() => setAccessGranted(true)} />;

  if (showOnboarding || !profile) return <Onboarding onComplete={saveProfile} existing={profile} C={C} darkMode={darkMode} />;

  if (!appReady) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          {darkMode ? <LogoInk height={42} /> : <LogoWarmWhite height={42} />}
        </div>
        <div style={{ width: 32, height: 32, border: `3px solid rgba(42,127,111,0.15)`, borderTopColor: C.teal, borderRadius: "50%", animation: "spin 0.9s linear infinite", margin: "0 auto" }} />
      </div>
    </div>
  );

  // ── WELCOME BACK SCREEN ────────────────────────────────────────────────
  if (showWelcome) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
        .welcome-card { animation: fadeUp 0.5s ease forwards; }
        .welcome-btn:hover { opacity: 0.88; transform: translateY(-1px); }
      `}</style>
      <div className="welcome-card" style={{ maxWidth: 440, width: "100%", textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
          {darkMode ? <LogoInk height={52} /> : <LogoWarmWhite height={52} />}
        </div>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: "2rem", color: C.text, margin: "0 0 10px", letterSpacing: -0.5, lineHeight: 1.2 }}>
          Welcome back, {profile?.name?.split(" ")[0] || "there"}.
        </h1>
        <p style={{ color: C.textSub, fontSize: "0.88rem", margin: "0 0 32px", fontWeight: 300, lineHeight: 1.7 }}>
          Your command center is ready.
        </p>

        {/* Stats row */}
        {applications.length > 0 && (
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 32 }}>
            {[
              { label: "Applied", val: applications.filter(a => ["applied","interview","offer"].includes(a.status)).length, color: C.tealBright },
              { label: "Interviews", val: applications.filter(a => ["interview","offer"].includes(a.status)).length, color: "#f0b429" },
              { label: "Saved", val: applications.filter(a => a.status === "saved").length, color: C.bluGray },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 10px", textAlign: "center" }}>
                <div style={{ fontFamily: "'Fraunces', serif", fontSize: "1.6rem", color, lineHeight: 1, marginBottom: 4 }}>{val}</div>
                <div style={{ fontSize: "0.62rem", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Primary CTA */}
        <button
          className="welcome-btn"
          onClick={() => setShowWelcome(false)}
          style={{ width: "100%", padding: "15px", background: C.teal, color: "#fff", border: "none", borderRadius: 10, fontSize: "0.95rem", fontWeight: 600, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", transition: "all 0.2s", marginBottom: 12, letterSpacing: "0.01em" }}
        >
          {applications.length > 0 ? "Continue your search →" : "Start your search →"}
        </button>

        {/* Secondary — go straight to tracker */}
        {applications.length > 0 && (
          <button
            className="welcome-btn"
            onClick={() => { setShowWelcome(false); setTab("tracker"); }}
            style={{ width: "100%", padding: "13px", background: "transparent", color: C.teal, border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: "0.88rem", fontWeight: 500, fontFamily: "'DM Sans', sans-serif", cursor: "pointer", transition: "all 0.2s" }}
          >
            ◈ View my tracker ({applications.length} application{applications.length !== 1 ? "s" : ""})
          </button>
        )}

        <p style={{ color: C.textMuted, fontSize: "0.68rem", marginTop: 24, fontStyle: "italic" }}>
          Fit First. Apply Fast.
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", background: C.bg, color: C.text, minHeight: "100vh", paddingBottom: 80 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; } textarea { resize: vertical; }
        input::placeholder, textarea::placeholder { color: ${C.textMuted}; }
        select option { background: ${C.bg}; color: ${C.text}; }
        input:focus, textarea:focus, select:focus { border-color: ${C.teal} !important; outline: none; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-4px)} 40%,80%{transform:translateX(4px)} }
        @keyframes modalIn { from{opacity:0;transform:scale(0.92) translateY(12px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 0 rgba(42,127,111,0.5)} 50%{box-shadow:0 0 0 10px rgba(42,127,111,0)} }
        button:hover { opacity: 0.88; }
        ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: rgba(42,127,111,0.3); border-radius: 3px; }
      `}</style>

      {/* Loading Modal */}
      {loading && <LoadingModal C={C} darkMode={darkMode} />}

      {/* Clarity Modal */}
      {showClarity && result && !loading && (
        <ClarityModal
          result={result}
          profile={profile}
          onSave={saveToTracker}
          onSaveLater={() => saveToTracker("saved")}
          onClose={() => setShowClarity(false)}
          onViewQuickAnswers={() => { setShowClarity(false); setTab("quickanswers"); }}
          C={C}
        />
      )}

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>

        {/* HEADER */}
        <div style={{ padding: "32px 0 24px", borderBottom: `1px solid ${C.border}`, marginBottom: 28, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
          <div>
            <div style={{ marginBottom: 6, overflow: "visible", transform: darkMode ? "translateY(-4px)" : "translateY(-2px)" }}>
              {darkMode ? <LogoInk height={36} /> : <LogoWarmWhite height={36} />}
            </div>
            <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.6rem", letterSpacing: -0.5, lineHeight: 1, margin: 0, color: C.text }}>Hey, {profile.name?.split(" ")[0] || "there"}.</h1>
            <p style={{ color: C.textSub, fontSize: "0.82rem", marginTop: 5, fontWeight: 300 }}>
              {ROLE_OPTIONS.filter(r => profile.roles?.includes(r.id)).map(r => r.label).slice(0,2).join(" · ") || "Job Search"} &nbsp;·&nbsp; ${profile.rate}/hr &nbsp;·&nbsp; {profile.workType}
            </p>
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            {[
              ["Applied", stats.applied, C.tealBright, "Total jobs you've applied to"],
              ["Interviews", stats.interviews, "#f0b429", "Jobs at interview or offer stage"],
              ["Saved", stats.saved, C.bluGray, "Jobs saved to review later"]
            ].map(([label, val, color, tip]) => (
              <Tooltip key={label} text={tip} C={C}>
                <div style={{ textAlign: "center", cursor: "default" }}>
                  <div style={{ fontFamily: "'Fraunces',serif", fontSize: "1.4rem", color, lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: "0.63rem", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 3 }}>{label}</div>
                </div>
              </Tooltip>
            ))}
            {/* Dark/Light mode toggle */}
            <Tooltip text={darkMode ? "Switch to light mode" : "Switch to dark mode"} C={C}>
              <button onClick={toggleDarkMode} style={{ width: 36, height: 36, borderRadius: 9, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, cursor: "pointer", fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}>
                {darkMode ? "☀" : "🌙"}
              </button>
            </Tooltip>
            <Tooltip text="Edit your profile — update your rate, skills, resume, and preferences" C={C}>
              <button onClick={() => setShowOnboarding(true)} style={{ width: 36, height: 36, borderRadius: 9, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, cursor: "pointer", fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}>⚙</button>
            </Tooltip>
          </div>
        </div>

        {/* TABS */}
        <div style={{ display: "flex", gap: 3, marginBottom: 28, background: C.card, borderRadius: 12, padding: 4 }}>
          {[
            ["generate","✦ Generate","Analyze a job posting and generate your resume, cover letter, and more"],
            ["tracker","◈ Tracker","Track all your job applications and their status"],
            ["quickanswers","▸ Quick Answers","Pre-written answers to common application questions"],
            ["guide","◎ Guide","How to read your scores and find the best jobs"]
          ].map(([id, label, tip]) => (
            <Tooltip key={id} text={tip} C={C}>
              <button onClick={() => setTab(id)} style={{ flex: 1, padding: "10px 10px", border: "none", borderRadius: 9, background: tab === id ? C.teal : "transparent", color: tab === id ? "#fff" : C.textMuted, fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 500, cursor: "pointer", transition: "all 0.2s" }}>{label}</button>
            </Tooltip>
          ))}
        </div>

        {/* ── GENERATE ── */}
        {tab === "generate" && (
          <div style={{ animation: "fadeUp 0.3s ease" }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "11px 16px", marginBottom: 22, fontSize: "0.83rem", color: C.textSub, lineHeight: 1.65 }}>
              <span style={{ color: C.tealBright, fontWeight: 600 }}>How it works:</span> Paste a job description → get a reality check, fit score, keyword analysis, your one-page resume, cover letter, and follow-up — all built from your profile.
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {[["Company Name", company, setCompany, "e.g. Acme Corp"], ["Job Title", jobtitle, setJobtitle, "e.g. Virtual Assistant"]].map(([label, val, setter, ph]) => (
                <div key={label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 17 }}>
                  <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 8 }}>{label}</label>
                  <input value={val} onChange={e => setter(e.target.value)} placeholder={ph} style={inputStyle} />
                </div>
              ))}
              <div style={{ gridColumn: "1/-1", background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 17 }}>
                <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 8 }}>Paste Job Description</label>
                <textarea value={jobdesc} onChange={e => setJobdesc(e.target.value)} rows={9} placeholder="Paste the full job description here — including hours, pay rate, and communication expectations…" style={inputStyle} />
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: "block", fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, marginBottom: 6 }}>Job Posting URL <span style={{ color: C.textMuted, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                  <input value={jobLink} onChange={e => setJobLink(e.target.value)} placeholder="https://…" style={{ ...inputStyle, fontFamily: "'DM Sans', sans-serif" }} />
                </div>
                <div style={{ marginTop: 10, fontSize: "0.72rem", color: C.textMuted, lineHeight: 1.5 }}>
                  JobLanded will automatically recommend the best resume style for this role — ATS, Traditional, or Modern.
                </div>
              </div>
            </div>

            {/* Cover letter detection banner */}
            {jobdesc.trim() && /cover letter (is )?(required|must|needed|mandatory|should be included)|please (include|attach|submit) (a )?cover letter|cover letter required|without a cover letter/i.test(jobdesc) && (
              <div style={{ marginTop: 14, padding: "11px 16px", background: "rgba(42,127,111,0.07)", border: "1px solid rgba(42,127,111,0.25)", borderRadius: 10, fontSize: "0.83rem", color: C.tealBright, lineHeight: 1.6, display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={{ fontSize: "1rem", flexShrink: 0 }}>✉</span>
                <div><strong>Cover letter required</strong> — this job posting asks for one. JobLanded will generate it automatically when you analyze.</div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
              <Tooltip text="Analyze this job posting against your profile — generates resume, cover letter, and more" C={C}>
                <button onClick={generate} disabled={loading || !jobdesc.trim()} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "12px 22px", borderRadius: 9, border: "none", background: loading ? `rgba(42,127,111,0.3)` : C.teal, color: "#fff", fontFamily: "inherit", fontSize: "0.9rem", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", transition: "all 0.2s" }}>
                  {loading ? "Analyzing…" : "✦ Analyze & Generate"}
                </button>
              </Tooltip>
              <Tooltip text="Clear the form and start with a new job posting" C={C}>
                <button onClick={() => { setCompany(""); setJobtitle(""); setJobdesc(""); setJobLink(""); setResult(null); setError(""); setShowClarity(false); }} style={{ padding: "12px 22px", borderRadius: 9, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontFamily: "inherit", fontSize: "0.9rem", fontWeight: 600, cursor: "pointer" }}>Clear</button>
              </Tooltip>
            </div>

            {error && <div style={{ marginTop: 14, padding: "13px 17px", background: "rgba(180,80,80,0.07)", border: "1px solid rgba(180,80,80,0.2)", borderRadius: 10, fontSize: "0.85rem", color: "#c97070" }}><strong>Error:</strong> {error}</div>}

            {result && !loading && !showClarity && (
              <div style={{ animation: "slideUp 0.4s ease" }}>
                <div style={{ height: 1, background: C.border, margin: "28px 0" }} />

                {/* Template recommendation banner */}
                {result.recommendedTemplate && (
                  <div style={{ marginBottom: 18, padding: "10px 16px", background: "rgba(42,127,111,0.06)", border: `1px solid rgba(42,127,111,0.18)`, borderRadius: 10, display: "flex", alignItems: "center", gap: 10, fontSize: "0.82rem" }}>
                    <span style={{ flexShrink: 0, fontSize: "0.9rem" }}>◈</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ color: C.tealBright, fontWeight: 600 }}>
                        {formatTemplateLabel(result.recommendedTemplate)} recommended for this role
                      </span>
                      <span style={{ color: C.textMuted, marginLeft: 6 }}>— {result.recommendedReason}</span>
                    </div>
                    {result.recommendedConfidence === "medium" && (
                      <span style={{ fontSize: "0.68rem", color: C.textMuted, fontStyle: "italic", flexShrink: 0 }}>You can override in the download section</span>
                    )}
                  </div>
                )}

                {result.realityFlags && (
                  <div style={{ borderRadius: 12, padding: "14px 18px", marginBottom: 18, background: "rgba(180,80,80,0.06)", border: "1px solid rgba(180,80,80,0.2)", animation: "shake 0.4s ease" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span>🚩</span>
                      <h4 style={{ flex: 1, fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#c97070", margin: 0 }}>Job Reality Check — Review Before Applying</h4>
                      <CopyButton text={result.realityFlags} small C={C} />
                    </div>
                    <div style={{ fontSize: "0.85rem", lineHeight: 1.85, color: C.textSub, whiteSpace: "pre-wrap" }}>{result.realityFlags}</div>
                  </div>
                )}

                {result.keywords && (
                  <div style={{ borderRadius: 12, padding: "14px 18px", marginBottom: 18, background: `rgba(42,127,111,0.05)`, border: `1px solid rgba(42,127,111,0.2)` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
                      <span style={{ fontSize: "0.9rem" }}>✦</span>
                      <h4 style={{ flex: 1, fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: C.teal, margin: 0 }}>Get Seen Keywords</h4>
                      <CopyButton text={result.keywords} small C={C} />
                    </div>
                    <div style={{ fontSize: "0.86rem", lineHeight: 1.9, color: C.textSub, whiteSpace: "pre-wrap" }}>{result.keywords}</div>
                  </div>
                )}

                {result.keyNotes && (
                  <div style={{ borderRadius: 12, padding: "14px 18px", marginBottom: 18, background: result.hasWarnings ? "rgba(240,180,40,0.04)" : "rgba(42,127,111,0.04)", border: `1px solid ${result.hasWarnings ? "rgba(240,180,40,0.2)" : "rgba(42,127,111,0.18)"}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 9 }}>
                      <span>{result.hasWarnings ? "⚠" : "✓"}</span>
                      <h4 style={{ flex: 1, fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: result.hasWarnings ? "#f0b429" : C.tealBright, margin: 0 }}>Key Things to Note Before Applying</h4>
                      <CopyButton text={result.keyNotes} small C={C} />
                    </div>
                    <div style={{ fontSize: "0.86rem", lineHeight: 1.85, color: C.textSub, whiteSpace: "pre-wrap" }}>{result.keyNotes}</div>
                  </div>
                )}

                {/* Fit Score Card */}
                <div style={{ borderRadius: 14, padding: "19px 23px", display: "flex", alignItems: "center", gap: 19, marginBottom: 18, flexWrap: "wrap", background: fitColors[result.level].bg, border: `1px solid ${fitColors[result.level].border}` }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0, background: fitColors[result.level].circle, border: `2px solid ${fitColors[result.level].circleBorder}` }}>
                    <span style={{ fontFamily: "'Fraunces',serif", fontSize: "1.55rem", color: fitColors[result.level].text, lineHeight: 1 }}>{result.score}</span>
                    <span style={{ fontSize: "0.6rem", color: fitColors[result.level].text, textTransform: "uppercase", letterSpacing: "0.06em" }}>/ 100</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: "1.1rem", color: C.text, marginBottom: 5 }}>{result.verdict}</div>
                    <div style={{ fontSize: "0.84rem", color: C.textSub, lineHeight: 1.7 }}>{result.reasoning}</div>
                  </div>
                  <button onClick={() => setShowClarity(true)} style={{ padding: "10px 18px", borderRadius: 9, border: `1px solid ${C.teal}`, background: "rgba(42,127,111,0.12)", color: C.tealBright, fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer" }}>View Summary →</button>
                </div>

                {/* Resume & Cover Letter */}
                {result.resumeFull && (
                  <ResultSection
                    title="Your Tailored Resume"
                    content={result.resumeFull}
                    showDocx
                    docxFilename="Resume.docx"
                    profile={profile}
                    lastTemplate={lastTemplate}
                    recommendedTemplate={result.recommendedTemplate}
                    recommendedReason={result.recommendedReason}
                    onTemplateUsed={setLastTemplate}
                    docType="resume"
                    C={C}
                  />
                )}
                {result.cover && (
                  <ResultSection title="Cover Letter" content={result.cover} showDocx docxFilename="CoverLetter.docx" profile={profile} lastTemplate={lastTemplate} onTemplateUsed={setLastTemplate} docType="cover" C={C} />
                )}
                {result.followup && (
                  <div style={{ marginBottom: 22 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <h4 style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: C.teal, margin: 0 }}>Follow-Up Email</h4>
                      <div style={{ display: "flex", gap: 7 }}>
                        <button onClick={() => downloadText(`FollowUp_${result.company || "Application"}.txt`, result.followup)} style={{ background: `rgba(42,127,111,0.08)`, border: `1px solid ${C.border}`, color: C.textSub, padding: "5px 12px", borderRadius: 7, fontSize: "0.72rem", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>↓ Download</button>
                        <CopyButton text={result.followup} C={C} />
                      </div>
                    </div>
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", fontSize: "0.88rem", lineHeight: 1.9, color: C.textSub, whiteSpace: "pre-wrap" }}>{result.followup}</div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", paddingTop: 10 }}>
                  <Tooltip text="Mark as applied and save to your tracker" C={C}>
                    <button onClick={() => saveToTracker("applied")} style={{ padding: "12px 20px", borderRadius: 9, border: "none", background: C.teal, color: "#fff", fontFamily: "inherit", fontSize: "0.88rem", fontWeight: 600, cursor: "pointer" }}>✓ Applied — Save to Tracker</button>
                  </Tooltip>
                  <Tooltip text="Save to review and apply later" C={C}>
                    <button onClick={() => saveToTracker("saved")} style={{ padding: "12px 20px", borderRadius: 9, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontFamily: "inherit", fontSize: "0.88rem", fontWeight: 600, cursor: "pointer" }}>◈ Save for Later</button>
                  </Tooltip>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TRACKER ── */}
        {tab === "tracker" && (
          <div style={{ animation: "fadeUp 0.3s ease" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["all","applied","interview","offer","saved","declined","followup"].map(f => (
                  <button key={f} onClick={() => setFilter(f)} style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${filter === f ? C.teal : C.border}`, background: filter === f ? `rgba(42,127,111,0.12)` : "transparent", color: filter === f ? C.tealBright : C.textMuted, fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 500, cursor: "pointer" }}>
                    {f === "followup" ? "⏰ Follow-Up Due" : f.charAt(0).toUpperCase() + f.slice(1)}
                    {f === "all" && ` (${applications.length})`}
                  </button>
                ))}
              </div>
              <Tooltip text="Export your application history as a CSV file" C={C}>
                <button onClick={exportCSV} disabled={applications.length === 0} style={{ padding: "7px 16px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontFamily: "inherit", fontSize: "0.78rem", cursor: "pointer" }}>↓ Export CSV</button>
              </Tooltip>
            </div>

            {filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "52px 20px", color: C.textMuted }}>
                <div style={{ fontSize: "2rem", marginBottom: 14 }}>◈</div>
                <div style={{ fontSize: "0.9rem" }}>No applications here yet.</div>
                <div style={{ fontSize: "0.8rem", marginTop: 6 }}>Generate and save your first job application to get started.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {filtered.map(app => (
                  <div key={app.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    <div style={{ width: 48, height: 48, borderRadius: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: fitColors[app.level || "medium"].circle, border: `1.5px solid ${fitColors[app.level || "medium"].circleBorder}`, flexShrink: 0 }}>
                      <span style={{ fontFamily: "'Fraunces',serif", fontSize: "1.1rem", color: fitColors[app.level || "medium"].text, lineHeight: 1 }}>{app.score}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.92rem", color: C.text, marginBottom: 2 }}>{app.jobtitle || "Untitled Role"}{app.company ? ` — ${app.company}` : ""}</div>
                      <div style={{ fontSize: "0.72rem", color: C.textMuted }}>
                        {new Date(app.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        {app.status === "applied" && (Date.now() - new Date(app.date)) / 86400000 >= 5 && <span style={{ color: "#f0b429", marginLeft: 8 }}>⏰ Follow-up due</span>}
                      </div>
                      {app.jobLink && (
                        <a href={app.jobLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.72rem", color: C.teal, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, marginTop: 3 }}>
                          ↗ View job posting
                        </a>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <select value={app.status} onChange={e => { const updated = applications.map(a => a.id === app.id ? { ...a, status: e.target.value } : a); saveApps(updated); }} style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: statusConfig[app.status]?.bg || C.card, color: statusConfig[app.status]?.color || C.textSub, fontFamily: "inherit", fontSize: "0.78rem", cursor: "pointer", outline: "none" }}>
                        {Object.entries(statusConfig).map(([val, cfg]) => <option key={val} value={val}>{cfg.label}</option>)}
                      </select>
                      <Tooltip text="View resume, cover letter, and all documents" C={C}>
                        <button onClick={() => setModalApp(app)} style={{ padding: "6px 14px", borderRadius: 7, border: `1px solid ${C.border}`, background: "transparent", color: C.textSub, fontFamily: "inherit", fontSize: "0.78rem", cursor: "pointer" }}>View Docs</button>
                      </Tooltip>
                      <Tooltip text="Remove this application from your tracker" C={C}>
                        <button onClick={() => { if (window.confirm("Remove this application?")) saveApps(applications.filter(a => a.id !== app.id)); }} style={{ padding: "6px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, fontFamily: "inherit", fontSize: "0.78rem", cursor: "pointer" }}>✕</button>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── QUICK ANSWERS ── */}
        {tab === "quickanswers" && (
          <div style={{ animation: "fadeUp 0.3s ease" }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "11px 16px", marginBottom: 18, fontSize: "0.83rem", color: C.textSub, lineHeight: 1.65 }}>
              <span style={{ color: C.tealBright, fontWeight: 600 }}>Application form questions? Already answered.</span> Every answer below is built from your profile. Click to copy and paste directly into any application form.
            </div>
            {quickAnswers.map((cat, ci) => {
              const isOpen = openQA === ci;
              return (
                <div key={ci} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, marginBottom: 10, overflow: "hidden" }}>
                  <button onClick={() => setOpenQA(isOpen ? null : ci)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "15px 18px", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                    <span style={{ fontSize: "1rem", color: C.teal }}>{cat.icon}</span>
                    <span style={{ flex: 1, fontWeight: 600, fontSize: "0.92rem", color: C.text }}>{cat.category}</span>
                    <span style={{ color: C.textMuted, fontSize: "0.8rem", transition: "transform 0.2s", display: "inline-block", transform: isOpen ? "rotate(90deg)" : "none" }}>▸</span>
                  </button>
                  {isOpen && (
                    <div style={{ borderTop: `1px solid ${C.border}` }}>
                      {cat.items.map((item, idx) => (
                        <div key={idx} style={{ padding: "14px 18px", borderBottom: idx < cat.items.length - 1 ? `1px solid ${C.border}` : "none" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 13, marginBottom: 7 }}>
                            <span style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: C.teal }}>{item.label}</span>
                            <CopyButton text={item.text} small C={C} />
                          </div>
                          <p style={{ fontSize: "0.86rem", color: C.textSub, lineHeight: 1.8, margin: 0 }}>{item.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ marginTop: 16, background: C.card, border: `1px solid ${C.border}`, borderRadius: 9, padding: "11px 14px", fontSize: "0.79rem", color: C.textMuted, lineHeight: 1.7 }}>
              <span style={{ color: C.teal, fontWeight: 600 }}>Note:</span> These pull from your profile. Rate, availability, or background changed? Tap ⚙ to update — these refresh automatically.
            </div>
          </div>
        )}

        {/* ── GUIDE ── */}
        {tab === "guide" && (
          <div style={{ animation: "fadeUp 0.3s ease" }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, marginBottom: 16 }}>
              <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.05rem", margin: "0 0 14px", color: C.text }}>Reading Your Scores</h3>
              {[["🟢","75–100","Strong match. Apply with the full cover letter.",C.tealBright,"rgba(42,127,111,0.06)"],["🟡","50–74","Decent but something is off — read the reasoning.","#f0b429","rgba(240,180,40,0.05)"],["🔴","0–49","Doesn't match your targets. Your time is worth more.","#c97070","rgba(180,80,80,0.04)"]].map(([e,r,d,c,bg]) => (
                <div key={r} style={{ background: bg, borderRadius: 9, padding: "10px 13px", display: "flex", gap: 11, alignItems: "flex-start", marginBottom: 8 }}>
                  <span style={{ flexShrink: 0 }}>{e}</span>
                  <div><span style={{ fontWeight: 700, color: c, fontSize: "0.87rem" }}>{r}</span><span style={{ color: C.textMuted, fontSize: "0.82rem", marginLeft: 8 }}>{d}</span></div>
                </div>
              ))}
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, marginBottom: 16 }}>
              <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.05rem", margin: "0 0 14px", color: C.text }}>Understanding Your Get Seen Keywords</h3>
              <p style={{ fontSize: "0.82rem", color: C.textMuted, lineHeight: 1.7, margin: "0 0 14px" }}>When employers post jobs, they look for specific words and phrases that match what they need. JobLanded finds those words and tells you exactly where you stand.</p>
              {[["✓ Covered","These keywords are already in your resume. Make sure they appear exactly as listed — the more that match, the more likely you are to be seen.",C.tealBright],["⚠ Add If You Have It","Skills or tools mentioned in the job description that aren't in your profile yet. If you genuinely have this experience, add it to your resume before applying.","#f0b429"],["✕ Not in Profile","Requirements you don't appear to have. Know this going in — it doesn't mean don't apply, it means apply with eyes open.","#c97070"]].map(([label, desc, color]) => (
                <div key={label} style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.87rem", color, marginBottom: 5 }}>{label}</div>
                  <div style={{ fontSize: "0.82rem", color: C.textMuted, lineHeight: 1.7 }}>{desc}</div>
                </div>
              ))}
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, marginBottom: 16 }}>
              <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.05rem", margin: "0 0 14px", color: C.text }}>Universal Red Flags</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
                {[`"Wear many hats"`,`"Fast-paced environment"`,"Urgent response required","Must be available immediately","High meeting culture","Vague or expanding scope","Full-time hours for part-time pay",`"Startup energy"`].map(f => (
                  <div key={f} style={{ background: "rgba(180,80,80,0.04)", border: "1px solid rgba(180,80,80,0.1)", borderRadius: 8, padding: "7px 11px", fontSize: "0.8rem", color: "#c97070" }}>⚠ {f}</div>
                ))}
              </div>
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24 }}>
              <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.05rem", margin: "0 0 15px", color: C.text }}>Best Places to Find Jobs</h3>
              {[
                ["Belay Solutions","PRIORITY","Apply once, matched with clients. Strong async fit.","https://belaysolutions.com/become-a-virtual-assistant"],
                ["Time Etc","PRIORITY","Apply once for ongoing async VA work.","https://web.timeetc.com/become-a-va"],
                ["LinkedIn","HIGH VOLUME","Filter by role type and work type. Set alerts.","https://linkedin.com/jobs"],
                ["FlexJobs","FLEXIBLE","Curated part-time and flexible remote listings.","https://flexjobs.com"],
                ["We Work Remotely","REMOTE ONLY","Every listing is genuinely remote.","https://weworkremotely.com"],
                ["Idealist","MISSION-DRIVEN","Nonprofits — often async-friendly and values-aligned.","https://idealist.org"],
              ].map(([name, tag, desc, url]) => (
                <div key={name} style={{ display: "flex", alignItems: "flex-start", gap: 13, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: "0.9rem", color: C.text }}>{name}</span>
                      <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", background: "rgba(42,127,111,0.12)", color: C.teal, padding: "2px 7px", borderRadius: 20 }}>{tag}</span>
                    </div>
                    <p style={{ fontSize: "0.81rem", color: C.textMuted, margin: 0, lineHeight: 1.65 }}>{desc}</p>
                  </div>
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: C.tealBright, fontSize: "0.77rem", fontWeight: 500, textDecoration: "none", whiteSpace: "nowrap", paddingTop: 2 }}>Visit →</a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {modalApp && <DocModal app={modalApp} onClose={() => setModalApp(null)} profile={profile} C={C} />}

      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: "#1C2B3A", border: `1px solid rgba(42,127,111,0.35)`, borderRadius: 11, padding: "11px 16px", fontSize: "0.85rem", color: "#F8F5F3", animation: "slideUp 0.3s ease", zIndex: 200, boxShadow: "0 4px 24px rgba(0,0,0,0.3)" }}>{toast}</div>
      )}
    </div>
  );
}
